import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Users, ListTodo, BarChart3, Filter, Plus, Search, X, Check, RefreshCw,
  MessageCircle, Phone, AlertCircle, Clock, Calendar, ChevronRight, Trash2, KeyRound,
  Copy, ExternalLink, UserPlus, Send
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import "./AdminCrm.mobile.css";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors as useDndSensors, useDroppable, closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import WhatsAppChat from "../../components/WhatsAppChat";
import HelpVideoButton from "../../components/HelpVideoButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VIP_COLOR = { Diamond: "#B9F2FF", Platinum: "#E8E8E8", Gold: "#C5A059", Silver: "#C0C0C0", Standard: "#888" };

// Per-manager colour palette — deterministic mapping so every manager gets a
// consistent pastel row-tint across sessions. Admins/seniors scan the CRM by
// colour instead of hunting for names in the manager column.
const MGR_PALETTE = [
  { bg: "rgba(59,130,246,0.10)", stripe: "#3B82F6" },  // blue
  { bg: "rgba(16,185,129,0.10)", stripe: "#10B981" },  // emerald
  { bg: "rgba(217,119,6,0.10)",  stripe: "#D97706" },  // amber
  { bg: "rgba(219,39,119,0.10)", stripe: "#DB2777" },  // pink
  { bg: "rgba(139,92,246,0.10)", stripe: "#8B5CF6" },  // violet
  { bg: "rgba(6,182,212,0.10)",  stripe: "#06B6D4" },  // cyan
  { bg: "rgba(234,88,12,0.10)",  stripe: "#EA580C" },  // orange
  { bg: "rgba(20,184,166,0.10)", stripe: "#14B8A6" },  // teal
  { bg: "rgba(220,38,38,0.10)",  stripe: "#DC2626" },  // red
  { bg: "rgba(101,163,13,0.10)", stripe: "#65A30D" },  // lime
];
const _hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const managerColor = (id) => id ? MGR_PALETTE[_hashStr(id) % MGR_PALETTE.length]
                                : { bg: "transparent", stripe: "transparent" };

const fmtMoney = (n) => `${Math.floor(n || 0).toLocaleString("ru-RU")} ₸`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtRelative = (s) => {
  if (!s) return "—";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60) return "только что";
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  return `${Math.floor(diff / 86400)} дн назад`;
};

/**
 * Открытая карточка лида запоминается на время вкладки: если браузер на
 * телефоне перезагрузит страницу (iOS часто выгружает вкладки из памяти),
 * менеджер вернётся в тот же лид, а не в общий список.
 */
function usePersistedSelection(key) {
  const [selectedId, setSelectedId] = useState(() => {
    try { return sessionStorage.getItem(key) || null; } catch { return null; }
  });
  useEffect(() => {
    try {
      if (selectedId) sessionStorage.setItem(key, selectedId);
      else sessionStorage.removeItem(key);
    } catch { /* приватный режим — просто без восстановления */ }
  }, [key, selectedId]);
  return [selectedId, setSelectedId];
}

// WhatsApp helpers — used across the CRM.
// waLink(phone)                    → opens the WhatsApp chat (no message)
// waLink(phone, { text })          → chat with a pre-filled message
// waCallLink(phone)                → tries to trigger a WhatsApp voice call.
//   • Mobile devices honour `whatsapp://call?phone=…` and open the dialer.
//   • Desktop/browsers ignore the whatsapp:// scheme, so we fall back to
//     the standard chat URL — one click away from the phone icon on
//     WhatsApp Web / Desktop.
const _digits = (phone) => (phone || "").replace(/[^\d]/g, "");
const _isMobileDevice = () => {
  try { return /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent || ""); }
  catch { return false; }
};
const waLink = (phone, opts = {}) => {
  const qs = opts.text ? `?text=${encodeURIComponent(opts.text)}` : "";
  return `https://wa.me/${_digits(phone)}${qs}`;
};
const waCallLink = (phone) => {
  const num = _digits(phone);
  return _isMobileDevice() ? `whatsapp://call?phone=${num}` : `https://wa.me/${num}`;
};
const WA_GREETING = "Здравствуйте";

export default function AdminCrm() {
  const [meta, setMeta] = useState(null);
  const [managers, setManagers] = useState([]);
  // Default tab: managers land on «Мои сегодня» (utility-first),
  // admins/seniors land on «Клиенты» (overview-first).
  const [tab, setTab] = useState(() => {
    try { return sessionStorage.getItem("crm_tab") || "clients"; }
    catch { return "clients"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("crm_tab", tab); } catch { /* noop */ }
  }, [tab]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/crm/meta`, { withCredentials: true }),
      axios.get(`${API}/admin/crm/managers`, { withCredentials: true }),
    ]).then(([m, mg]) => {
      setMeta(m.data);
      setManagers(mg.data);
      let saved = null;
      try { saved = sessionStorage.getItem("crm_tab"); } catch { /* noop */ }
      if (m.data?.role === "manager" && !saved) setTab("today");
    }).catch(console.error);
  }, []);

  // Плашка «Задачи на сегодня» открывает CRM сразу на нужной вкладке.
  useEffect(() => {
    const handler = (ev) => {
      const t = ev?.detail?.crm_tab;
      if (t) setTab(t);
    };
    window.addEventListener("admin:navigate", handler);
    return () => window.removeEventListener("admin:navigate", handler);
  }, []);

  if (!meta) return <div style={{ padding: "40px", textAlign: "center", color: "#999", fontFamily: "Manrope" }}>Загрузка CRM…</div>;

  return (
    <div data-testid="admin-crm" style={{ paddingBottom: "32px" }}>
      {/* Sub-tabs */}
      <div className="crm-tabs-strip" style={{ display: "flex", gap: "4px", marginBottom: "22px", borderBottom: "1px solid #e5e7eb" }}>
        <TabBtn id="today" active={tab === "today"} onClick={setTab} icon={<Clock size={14} />}>Мои сегодня</TabBtn>
        <TabBtn id="clients" active={tab === "clients"} onClick={setTab} icon={<Users size={14} />}>Клиенты</TabBtn>
        <TabBtn id="queue" active={tab === "queue"} onClick={setTab} icon={<AlertCircle size={14} />}>Очередь</TabBtn>
        <TabBtn id="fitting" active={tab === "fitting"} onClick={setTab} icon={<Calendar size={14} />}>На примерку</TabBtn>
        <TabBtn id="pipeline" active={tab === "pipeline"} onClick={setTab} icon={<Filter size={14} />}>Воронка</TabBtn>
        <TabBtn id="tasks" active={tab === "tasks"} onClick={setTab} icon={<ListTodo size={14} />}>Задачи</TabBtn>
        <TabBtn id="stats" active={tab === "stats"} onClick={setTab} icon={<BarChart3 size={14} />}>Статистика</TabBtn>
        <div style={{ marginLeft: "auto", paddingBottom: "6px" }}>
          <HelpVideoButton slug="video-mini-crm" label="Как работать в CRM"
            testId="help-video-mini-crm" />
        </div>
      </div>

      {tab === "today" && <MyTodayPane meta={meta} managers={managers} />}
      {tab === "clients" && <ClientsPane meta={meta} managers={managers} />}
      {tab === "queue" && <UnassignedQueuePane meta={meta} managers={managers} />}
      {tab === "fitting" && <FittingTodayPane meta={meta} managers={managers} />}
      {tab === "pipeline" && <PipelinePane meta={meta} managers={managers} />}
      {tab === "tasks" && <TasksPane meta={meta} managers={managers} />}
      {tab === "stats" && <StatsPane meta={meta} />}
    </div>
  );
}

const TabBtn = ({ id, active, onClick, icon, children }) => (
  <button data-testid={`crm-tab-${id}`} onClick={() => onClick(id)}
          style={{
            padding: "10px 18px", border: "none", background: "none",
            borderBottom: `2px solid ${active ? "#C5A059" : "transparent"}`,
            color: active ? "#0A0A0A" : "#888", cursor: "pointer",
            fontFamily: "Manrope", fontSize: "13px", fontWeight: 600,
            display: "flex", alignItems: "center", gap: "6px",
            transition: "all 0.2s",
          }}>
    {icon} {children}
  </button>
);


// ============================================================================
// UNASSIGNED QUEUE — leads with no owner (admin/senior only)
// ============================================================================

function UnassignedQueuePane({ meta, managers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draining, setDraining] = useState(false);
  const [selectedId, setSelectedId] = usePersistedSelection('crm_open_queue');
  const [assignTarget, setAssignTarget] = useState(null);
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/unassigned-queue`,
                                 { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) {
      if (e.response?.status === 403) setItems([]);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const drainAll = async () => {
    if (!window.confirm(`Распределить ${items.length} лидов между активными онлайн-менеджерами?`)) return;
    setDraining(true);
    try {
      const r = await axios.post(`${API}/admin/crm/unassigned-queue/drain`, {},
                                   { withCredentials: true });
      alert(`Готово: распределено ${r.data.drained}, пропущено (никого на смене): ${r.data.skipped_no_managers}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось распределить");
    } finally { setDraining(false); }
  };

  if (!isPrivileged) {
    return <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontFamily: "Manrope" }}>
      Доступно только админам и старшим менеджерам
    </div>;
  }

  return (
    <div data-testid="unassigned-queue-pane">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                     marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
            Очередь без ответственного · <span data-testid="queue-count" style={{ color: "#C5A059" }}>{items.length}</span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Manrope" }}>
            Лиды, которые пришли пока никто из ONLINE-менеджеров не был на смене
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="queue-refresh-btn" onClick={load} style={btnSecondary}>
            <RefreshCw size={13} /> Обновить
          </button>
          <button data-testid="queue-drain-btn"
            onClick={drainAll}
            disabled={draining || items.length === 0}
            style={{ ...btnPrimary, opacity: (draining || items.length === 0) ? 0.5 : 1 }}>
            {draining ? "Распределяем…" : `Раздать очередь (${items.length})`}
          </button>
        </div>
      </div>
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty text="Очередь пустая — все лиды распределены" />
      ) : (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #e5e7eb" }}>
                <Th>Клиент</Th><Th>Телефон</Th><Th>Ждёт</Th><Th>Статус</Th><Th /></tr>
            </thead>
            <tbody>
              {items.map(c => {
                const w = c.wait_seconds || 0;
                const color = w > 900 ? "#DC2626" : w > 300 ? "#D97706" : "#16A34A";
                const waitLabel = w < 60 ? `${w}с` : w < 3600 ? `${Math.floor(w/60)}м` : `${Math.floor(w/3600)}ч ${Math.floor((w%3600)/60)}м`;
                return (
                  <tr key={c.id} data-testid={`queue-row-${c.id}`}
                      onClick={() => setSelectedId(c.id)}
                      style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
                    <Td className="crm-td-primary"><b>{c.name || "—"}</b></Td>
                    <Td>{c.phone}</Td>
                    <Td>
                      <span style={{ color, fontWeight: 700, fontSize: 12 }}>● {waitLabel}</span>
                    </Td>
                    <Td><StatusBadge color={c.crm_status_color} label={c.crm_status_label} /></Td>
                    <Td style={{ width: 200 }}>
                      <button data-testid={`queue-assign-${c.id}`}
                        onClick={(e) => { e.stopPropagation(); setAssignTarget(c); }}
                        style={{ ...btnPrimary, padding: "4px 12px", fontSize: 11 }}>
                        Назначить →
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selectedId && <ClientDetail clientId={selectedId} meta={meta} managers={managers} onClose={() => { setSelectedId(null); load(); }} />}
      {assignTarget && (
        <ReassignModal client={assignTarget} managers={managers}
          onClose={(saved) => { setAssignTarget(null); if (saved) load(); }} />
      )}
    </div>
  );
}


// ============================================================================

// ============================================================================
// MY TODAY — manager's morning worklist with 5 tabs
// ============================================================================

const TODAY_TABS = [
  { key: "new",         label: "Новые",       color: "#3b82f6", icon: AlertCircle },
  { key: "in_progress", label: "В работе",    color: "#8b5cf6", icon: MessageCircle },
  { key: "follow_up",   label: "Follow-up",   color: "#f59e0b", icon: Clock },
  { key: "today",       label: "Сегодня",     color: "#10b981", icon: Calendar },
  { key: "overdue",     label: "Просрочено",  color: "#dc2626", icon: AlertCircle },
];

function MyTodayPane({ meta, managers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("new");
  const [selectedId, setSelectedId] = usePersistedSelection('crm_open_today');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/my-today`, { withCredentials: true });
      setData(r.data);
      // If the user opens the pane with 0 new but overdue → snap to overdue.
      if (r.data.new.count === 0 && r.data.overdue.count > 0) setActive("overdue");
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);  // refresh every 60s
    return () => clearInterval(t);
  }, []);

  if (loading || !data) return <Loading />;

  const cfg = data.sla_settings || {};
  const bucket = data[active] || { count: 0, items: [] };
  const rows = active === "today" ? bucket.items :
               active === "overdue" ? [...(bucket.tasks || []), ...(bucket.sla_clients || [])] :
               bucket.items;

  return (
    <div data-testid="my-today-pane">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                     marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>Утро {new Date().toLocaleDateString("ru-RU", {day:"2-digit",month:"long"})}, {meta.name || "менеджер"}</div>
          <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            Кому написать сейчас · SLA {cfg.sla_first_response_minutes || 5} мин · авто-передача через {cfg.auto_reassign_minutes || 10} мин
          </div>
        </div>
        <button data-testid="today-refresh" onClick={load} style={btnSecondary}>
          <RefreshCw size={13} /> Обновить
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TODAY_TABS.map(t => (
          <button key={t.key}
            data-testid={`today-tab-${t.key}`}
            onClick={() => setActive(t.key)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px",
                     borderRadius: 8, border: active === t.key ? `2px solid ${t.color}` : "1px solid #e5e7eb",
                     background: active === t.key ? "white" : "#fafafa", cursor: "pointer",
                     fontFamily: "Manrope", fontSize: 13, fontWeight: active === t.key ? 700 : 500,
                     color: active === t.key ? "#0A0A0A" : "#374151", flex: "0 0 auto" }}>
            <t.icon size={14} style={{ color: t.color }} />
            {t.label}
            <span style={{ background: t.color, color: "white", padding: "1px 8px",
                            borderRadius: 999, fontSize: 11, fontWeight: 800 }}>
              {data[t.key]?.count || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Rows */}
      {rows.length === 0 ? (
        <div data-testid="today-empty"
          style={{ background: "white", border: "1px dashed #e5e7eb", borderRadius: 10,
                    padding: 40, textAlign: "center", fontFamily: "Manrope",
                    color: "#94a3b8", fontSize: 13 }}>
          {active === "new" && "✨ Никого нового пока — можно допилить старых клиентов"}
          {active === "in_progress" && "У вас нет активных диалогов"}
          {active === "follow_up" && "Все follow-up закрыты — красавчик"}
          {active === "today" && "На сегодня задач нет"}
          {active === "overdue" && "🎉 Ни одного просроченного клиента — идеально"}
        </div>
      ) : (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          {rows.map((row, i) => (
            <TodayRow key={row.id || row.task_id || i} row={row}
              onOpen={() => setSelectedId(row.user_id || row.id)} />
          ))}
        </div>
      )}

      {selectedId && <ClientDetail clientId={selectedId} meta={meta} managers={managers}
        onClose={() => { setSelectedId(null); load(); }} />}
    </div>
  );
}

function TodayRow({ row, onOpen }) {
  // Distinguish tasks (have `title`, `due_at`, `user_id`) from clients (have `phone`)
  const isTask = !!row.title && !!row.user_id;
  const sla = row.sla;
  const slaColor = sla ? (sla.status === "breached" ? "#DC2626"
                        : sla.status === "warn" ? "#F59E0B"
                        : sla.status === "ok" ? "#10B981" : "#94a3b8") : null;
  const slaLabel = sla && sla.seconds_left != null
    ? (sla.status === "ok" ? `${Math.floor(sla.seconds_left / 60)}м до SLA` :
       sla.status === "warn" ? "SLA ⚠️" :
       sla.status === "breached" ? "🔴 SLA нарушен" : "")
    : "";
  return (
    <div data-testid={`today-row-${row.id || row.task_id}`}
      onClick={onOpen}
      style={{ display: "flex", alignItems: "center", padding: "12px 14px",
                borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                gap: 12, fontFamily: "Manrope",
                background: sla?.status === "breached" ? "rgba(220,38,38,0.04)" : "white" }}>
      {slaColor && (
        <span style={{ width: 10, height: 10, borderRadius: "50%",
                        background: slaColor, flex: "0 0 auto",
                        boxShadow: sla?.status !== "ok" ? `0 0 6px ${slaColor}80` : "none" }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: "#0A0A0A",
                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {isTask ? row.title : row.name || "—"}
          {sla?.status === "breached" && (
            <span data-testid={`sla-breach-${row.id}`}
              style={{ marginLeft: 8, padding: "1px 7px", background: "#DC2626", color: "white",
                       fontSize: 10, fontWeight: 800, borderRadius: 4, letterSpacing: "0.05em" }}>
              🔴 SLA
            </span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>
          {isTask
            ? `${row._client?.name || "Клиент"} · ${row._client?.phone || ""}`
            : `${row.phone} · ${row.crm_status_label}`}
          {slaLabel && <> · <b style={{ color: slaColor }}>{slaLabel}</b></>}
        </div>
      </div>
      <ChevronRight size={16} color="#bbb" />
    </div>
  );
}

// ============================================================================
// FITTING TODAY — offline manager pre-visit briefing
// ============================================================================

function FittingTodayPane({ meta, managers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = usePersistedSelection('crm_open_fitting');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/fitting-today`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="fitting-today-pane">
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, alignItems: "baseline" }}>
        <div>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
            На примерку сегодня · <span data-testid="fitting-count" style={{ color: "#C5A059" }}>{items.length}</span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Manrope" }}>
            Готовим бутик к клиентам — что показывать и что уже знает online-менеджер
          </div>
        </div>
        <button data-testid="fitting-refresh" onClick={load} style={btnSecondary}>
          <RefreshCw size={13} /> Обновить
        </button>
      </div>
      {loading ? <Loading /> :
       items.length === 0 ? <Empty text="Сегодня записей на примерку нет" /> :
       items.map((it) => (
         <div key={it.appointment.id} data-testid={`fitting-card-${it.client.id}`}
           style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10,
                     padding: 18, marginBottom: 14, fontFamily: "Manrope",
                     cursor: "pointer" }}
           onClick={() => setSelectedId(it.client.id)}>
           <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
             <div>
               <div style={{ fontFamily: "Cormorant Garamond", fontSize: 20, color: "#0A0A0A" }}>{it.client.name}</div>
               <div style={{ fontSize: 12, color: "#6b7280" }}>{it.client.phone}</div>
             </div>
             <div style={{ textAlign: "right" }}>
               <div style={{ padding: "4px 12px", background: "#C5A059", color: "white",
                              borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                 {it.appointment.date} · {it.appointment.time || ""}
               </div>
               {it.ltv > 0 && (
                 <div style={{ fontSize: 11, color: "#065F46", marginTop: 4 }}>
                   LTV: <b>{Math.floor(it.ltv).toLocaleString("ru-RU")} ₸</b> · {it.purchases_count} покупок
                 </div>
               )}
             </div>
           </div>
           <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: 12, fontSize: 12, color: "#374151", padding: "10px 0",
                          borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9",
                          margin: "10px 0" }}>
             <FieldChip label="ONLINE менеджер"
               value={managers.find(m => m.id === it.client.crm_manager_id)?.name || "—"} />
             <FieldChip label="BOUTIQUE менеджер"
               value={managers.find(m => m.id === it.client.boutique_manager_id)?.name || "—"} />
             <FieldChip label="Размер" value={it.client.crm_size || "—"} />
             <FieldChip label="Интерес" value={it.client.crm_interested_in || "—"} />
             <FieldChip label="VIP" value={it.client.vip_level} />
             <FieldChip label="Статус" value={it.client.crm_status_label} />
           </div>
           {it.recent_notes && it.recent_notes.length > 0 && (
             <div style={{ marginTop: 8 }}>
               <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em",
                              color: "#8a8a89", fontWeight: 700, marginBottom: 4 }}>
                 Комментарии ONLINE-менеджера
               </div>
               {it.recent_notes.map((n, i) => (
                 <div key={i} style={{ fontSize: 12.5, color: "#0A0A0A",
                                        padding: "6px 10px", background: "#fafafa",
                                        borderLeft: "3px solid #C5A059", borderRadius: 4,
                                        marginBottom: 4 }}>{n}</div>
               ))}
             </div>
           )}
         </div>
       ))}
      {selectedId && <ClientDetail clientId={selectedId} meta={meta} managers={managers}
        onClose={() => { setSelectedId(null); load(); }} />}
    </div>
  );
}
const FieldChip = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.12em",
                    color: "#8a8a89", fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 12.5, color: "#0A0A0A", fontWeight: 500,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
  </div>
);


// CLIENTS
// ============================================================================

function ClientsPane({ meta, managers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "", manager_id: "", search: "",
    only_overdue: false, date_range: "", date_from: "", date_to: "",
  });
  const [selectedId, setSelectedId] = usePersistedSelection('crm_open_clients');
  const [showCreate, setShowCreate] = useState(false);
  const [showIssueOtp, setShowIssueOtp] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());   // client ids
  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.manager_id) params.manager_id = filters.manager_id;
    if (filters.search) params.search = filters.search;
    if (filters.only_overdue) params.only_overdue = true;
    // Период считаем на сервере: иначе при большой базе заявки за конкретный
    // день (например 30 сентября) не попадали в первые 200 записей.
    if (filters.date_range === "custom") {
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      params.limit = 2000;
    }
    try {
      const r = await axios.get(`${API}/admin/crm/clients`, { params, withCredentials: true });
      setItems(r.data.items || []);
      setSelected(new Set());
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [filters.status, filters.manager_id, filters.only_overdue,
     filters.date_range, filters.date_from, filters.date_to]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  // Client-side date filter — we want lead-creation dates (created_at) not
  // last-contact dates. Kept in JS so admins can flip presets without
  // waiting for a network round-trip.
  const dateFiltered = (() => {
    if (!filters.date_range) return items;
    if (filters.date_range === "custom") return items;   // период считает сервер
    const now = new Date();
    let since;
    if (filters.date_range === "today") since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    else if (filters.date_range === "yesterday") {
      since = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const until = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return items.filter(c => c.created_at && new Date(c.created_at) >= since && new Date(c.created_at) < until);
    }
    else if (filters.date_range === "7d") since = new Date(Date.now() - 7 * 86400_000);
    else if (filters.date_range === "30d") since = new Date(Date.now() - 30 * 86400_000);
    else return items;
    return items.filter(c => c.created_at && new Date(c.created_at) >= since);
  })();

  const allVisibleSelected = dateFiltered.length > 0 && dateFiltered.every(c => selected.has(c.id));
  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(dateFiltered.map(c => c.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <div>
      {/* Filters */}
      <div className="crm-filter-bar" style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginBottom: "18px" }}>
        <form onSubmit={onSearch} style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#aaa" }} />
          <input data-testid="crm-search-input" placeholder="Имя, телефон…"
                 value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                 style={{ padding: "8px 10px 8px 32px", borderRadius: "6px", border: "1px solid #e5e7eb",
                         fontFamily: "Manrope", fontSize: "13px", width: "220px" }} />
        </form>
        <select data-testid="crm-status-filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={selStyle}>
          <option value="">Все статусы</option>
          {meta.statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select data-testid="crm-date-filter" value={filters.date_range}
                onChange={(e) => setFilters({ ...filters, date_range: e.target.value })}
                style={selStyle}>
          <option value="">Всё время</option>
          <option value="today">Сегодня</option>
          <option value="yesterday">Вчера</option>
          <option value="7d">За 7 дней</option>
          <option value="30d">За 30 дней</option>
          <option value="custom">Конкретный день / период…</option>
        </select>
        {filters.date_range === "custom" && (
          <div data-testid="crm-date-custom" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <input data-testid="crm-date-from" type="date" value={filters.date_from || ""}
                   onChange={(e) => setFilters({ ...filters, date_from: e.target.value, date_to: filters.date_to || e.target.value })}
                   style={{ ...selStyle, padding: "7px 8px" }} />
            <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#9ca3af" }}>—</span>
            <input data-testid="crm-date-to" type="date" value={filters.date_to || ""}
                   onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                   style={{ ...selStyle, padding: "7px 8px" }} />
            {[
              { id: "today", label: "Этот день", days: 0 },
              { id: "yst", label: "Вчера", days: 1 },
              { id: "w", label: "Неделю назад", days: 7 },
            ].map(p => (
              <button key={p.id} data-testid={`crm-date-quick-${p.id}`} type="button"
                onClick={() => {
                  const d = new Date(Date.now() - p.days * 86400_000);
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  setFilters({ ...filters, date_range: "custom", date_from: iso, date_to: iso });
                }}
                style={{ background: "transparent", border: "1px dashed #d1d5db", borderRadius: 6,
                         padding: "5px 9px", cursor: "pointer", fontFamily: "Manrope",
                         fontSize: 11, color: "#6b7280" }}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        {isPrivileged && (
          <select data-testid="crm-manager-filter" value={filters.manager_id} onChange={(e) => setFilters({ ...filters, manager_id: e.target.value })}
                  style={selStyle}>
            <option value="">Все менеджеры</option>
            <option value="free">— Свободные —</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: "Manrope", fontSize: "12px", color: "#666", cursor: "pointer" }}>
          <input type="checkbox" checked={filters.only_overdue} onChange={(e) => setFilters({ ...filters, only_overdue: e.target.checked })} />
          Только просроченные
        </label>
        <div style={{ flex: 1 }} />
        <button data-testid="crm-refresh-btn" onClick={load} style={btnSecondary}>
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Обновить
        </button>
        <button data-testid="crm-issue-otp-btn" onClick={() => setShowIssueOtp(true)} style={btnSecondary}>
          <KeyRound size={13} /> Выдать код
        </button>
        <button data-testid="crm-create-lead-btn" onClick={() => setShowCreate(true)} style={btnPrimary}>
          <Plus size={13} /> Создать лид
        </button>
      </div>

      {/* Selection legend */}
      {isPrivileged && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                       marginBottom: 12, padding: "10px 14px",
                       background: selected.size > 0 ? "rgba(197,160,89,0.08)" : "#fafafa",
                       border: selected.size > 0 ? "1px solid #C5A059" : "1px solid #e5e7eb",
                       borderRadius: 8, fontFamily: "Manrope", fontSize: 12.5,
                       transition: "all .2s" }}>
          {selected.size === 0 ? (
            <span style={{ color: "#6b7280" }}>
              Кликните по широкой колонке-галочке слева (по всей ячейке) —
              появятся массовые действия
            </span>
          ) : (
            <>
              <b data-testid="crm-selected-count" style={{ color: "#7c5a1e" }}>
                Выбрано: {selected.size}
              </b>
              <button data-testid="crm-select-all-btn"
                onClick={toggleAll}
                style={{ background: "transparent", border: "1px solid #e5e7eb",
                          padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                          fontSize: 11, color: "#6b7280" }}>
                {allVisibleSelected ? "Снять всё" : `Выделить всё (${dateFiltered.length})`}
              </button>
              <button data-testid="crm-clear-selection"
                onClick={() => setSelected(new Set())}
                style={{ background: "transparent", border: "1px solid #e5e7eb",
                          padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                          fontSize: 11, color: "#6b7280" }}>
                Снять выбор
              </button>
              <div style={{ flex: 1 }} />
              <button data-testid="crm-bulk-assign-btn"
                onClick={() => setAssignOpen(true)}
                style={{ ...btnSecondary, padding: "8px 16px",
                          borderColor: "#C5A059", color: "#7c5a1e" }}>
                <UserPlus size={13} /> Назначить менеджера ({selected.size})
              </button>
              <button data-testid="crm-bulk-broadcast-btn"
                onClick={() => {
                  const phones = items.filter(c => selected.has(c.id) && c.phone).map(c => c.phone);
                  if (!phones.length) { alert("У выбранных клиентов нет телефонов"); return; }
                  try { sessionStorage.setItem("waba_prefill_numbers", phones.join("\n")); } catch { /* noop */ }
                  window.dispatchEvent(new CustomEvent("admin:navigate", { detail: { section: "broadcast" } }));
                }}
                style={{ ...btnSecondary, padding: "8px 16px",
                          borderColor: "#16a34a", color: "#15803d" }}>
                <Send size={13} /> Рассылка WhatsApp ({selected.size})
              </button>
              <button data-testid="crm-bulk-task-btn"
                onClick={() => setBulkOpen(true)}
                style={{ ...btnPrimary, padding: "8px 18px" }}>
                <ListTodo size={13} /> Массовая задача ({selected.size})
              </button>
            </>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <Loading />
      ) : dateFiltered.length === 0 ? (
        <Empty text="Клиентов в выбранных фильтрах не найдено" />
      ) : (
        <div className="crm-clients-table-wrap" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "auto" }}>
          <table className="crm-clients-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: "980px" }}>
            <thead>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #e5e7eb" }}>
                {isPrivileged && (
                  <Th style={{ width: 56, padding: 0 }}>
                    <div data-testid="crm-select-all-cell"
                      onClick={toggleAll}
                      title="Выделить всё / снять всё"
                      style={{ padding: "14px 16px", cursor: "pointer",
                                display: "flex", alignItems: "center",
                                justifyContent: "center" }}>
                      <input data-testid="crm-select-all"
                        type="checkbox" checked={allVisibleSelected}
                        onChange={toggleAll}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 17, height: 17, cursor: "pointer" }} />
                    </div>
                  </Th>
                )}
                <Th>Клиент</Th><Th>Телефон</Th><Th>VIP</Th><Th>Статус</Th>
                <Th>Менеджер (лид)</Th><Th>Закрыл сделку</Th>
                <Th>Поступил</Th>
                <Th>Размер</Th><Th>Интерес</Th><Th>Контакт</Th><Th />
              </tr>
            </thead>
            <tbody>
              {dateFiltered.map(c => {
                const mc = managerColor(c.crm_manager_id);
                const isSel = selected.has(c.id);
                return (
                <tr key={c.id} data-testid={`crm-client-row-${c.id}`}
                    onClick={() => setSelectedId(c.id)}
                    style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                              background: isSel ? "rgba(197,160,89,0.18)" : mc.bg,
                              borderLeft: `4px solid ${mc.stripe}` }}>
                  {isPrivileged && (
                    <Td data-testid={`crm-select-cell-${c.id}`}
                      style={{ width: 56, padding: 0 }}
                      onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}
                      title="Выделить лид">
                      {/* Широкая кликабельная зона: промахнуться мимо
                          крошечного квадратика больше нельзя, и карточка
                          лида при этом не открывается. */}
                      <div style={{ padding: "16px 16px", cursor: "pointer",
                                     display: "flex", alignItems: "center",
                                     justifyContent: "center",
                                     background: isSel ? "rgba(197,160,89,0.25)" : "transparent" }}>
                        <input data-testid={`crm-select-${c.id}`}
                          type="checkbox" checked={isSel}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(c.id)}
                          style={{ width: 17, height: 17, cursor: "pointer" }} />
                      </div>
                    </Td>
                  )}
                  <Td className="crm-td-primary"><div style={{ fontWeight: 600, display: "flex",
                        alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      {c.name || "—"}
                      {c.lead_score != null && (
                        <span data-testid={`crm-lead-score-${c.id}`}
                          title={c.lead_score_reason || "ИИ-оценка готовности приехать"}
                          style={{ fontFamily: "Manrope", fontSize: "10.5px", fontWeight: 800,
                                    padding: "2px 7px", borderRadius: "999px",
                                    color: c.lead_readiness === "hot" ? "#dc2626"
                                         : c.lead_readiness === "warm" ? "#d97706" : "#64748b",
                                    background: c.lead_readiness === "hot" ? "#fee2e2"
                                         : c.lead_readiness === "warm" ? "#fef3c7" : "#f1f5f9" }}>
                          {c.lead_readiness === "hot" ? "🔥 " : ""}{c.lead_score}
                        </span>
                      )}
                    </div>
                      {c.crm_lead && <span style={{ fontSize: "10px", color: "#999" }}>Лид без аккаунта</span>}</Td>
                  <Td label="Телефон"><a onClick={(e) => e.stopPropagation()} href={`tel:${c.phone}`} style={{ color: "#0A0A0A" }}>{c.phone}</a></Td>
                  <Td label="VIP"><span style={{ background: VIP_COLOR[c.vip_level] || "#888", color: "#0A0A0A",
                                     padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 700 }}>{c.vip_level}</span></Td>
                  <Td label="Статус"><StatusBadge color={c.crm_status_color} label={c.crm_status_label} /></Td>
                  <Td label="Менеджер">
                    {!c.crm_manager_id ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
                        <span data-testid={`crm-free-badge-${c.id}`}
                              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0",
                                       fontFamily: "Manrope", fontSize: "10px", fontWeight: 700,
                                       padding: "2px 8px", borderRadius: "10px", letterSpacing: "0.05em" }}>
                          СВОБОДНЫЙ
                        </span>
                        <button data-testid={`crm-claim-btn-${c.id}`}
                                onClick={(e) => { e.stopPropagation(); setClaimTarget(c); }}
                                style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                                         border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer",
                                         fontFamily: "Manrope", fontSize: "10.5px", fontWeight: 700,
                                         letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          Взять клиента
                        </button>
                      </div>
                    ) : isPrivileged ? (
                      <select data-testid={`crm-row-manager-select-${c.id}`}
                              value={c.crm_manager_id || ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={async (e) => {
                                e.stopPropagation();
                                const val = e.target.value;
                                try {
                                  await axios.patch(`${API}/admin/crm/clients/${c.id}`,
                                                    { crm_manager_id: val }, { withCredentials: true });
                                  load();
                                } catch (err) {
                                  alert("Ошибка: " + (err.response?.data?.detail || err.message));
                                }
                              }}
                              style={{ padding: "4px 8px", borderRadius: "4px",
                                       border: "1px solid #C5A059",
                                       background: "#fef7e5",
                                       fontFamily: "Manrope", fontSize: "11px", cursor: "pointer",
                                       maxWidth: "140px" }}>
                        <option value="">— Снять (в общий пул) —</option>
                        {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {managers.find(m => m.id === c.crm_manager_id)?.name || (c.crm_manager_id === meta.user_id ? "Вы" : "—")}
                      </span>
                    )}
                  </Td>
                  <Td label="Закрыл сделку">
                    <CloserCell client={c} meta={meta} managers={managers}
                      isPrivileged={isPrivileged} onSaved={load} />
                  </Td>
                  <Td label="Поступил" style={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}>
                    {c.created_at ? fmtDate(c.created_at) : "—"}
                  </Td>
                  <Td label="Размер">{c.crm_size || "—"}</Td>
                  <Td label="Интерес" style={{ color: "#666", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.crm_interested_in || "—"}
                  </Td>
                  <Td label="Контакт" style={{ color: "#999", fontSize: "12px" }}>{fmtRelative(c.crm_last_contact_at)}</Td>
                  <Td className="crm-td-chevron" style={{ width: "40px" }}>
                    <ChevronRight size={16} color="#bbb" />
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <ClientDetail clientId={selectedId} meta={meta} managers={managers} onClose={() => { setSelectedId(null); load(); }} />}
      {claimTarget && <ClaimModal client={claimTarget} meta={meta}
                                  onClose={(saved) => { setClaimTarget(null); if (saved) load(); }} />}
      {showCreate && <CreateLead managers={managers}
                                 onClose={(saved) => { setShowCreate(false); if (saved) load(); }}
                                 onOpenExisting={(id) => { setShowCreate(false); setSelectedId(id); load(); }} />}
      {showIssueOtp && <IssueOtpModal onClose={() => setShowIssueOtp(false)} />}
      {bulkOpen && (
        <BulkTaskModal
          clientIds={Array.from(selected)}
          clients={items.filter(c => selected.has(c.id))}
          managers={managers}
          onClose={(created) => {
            setBulkOpen(false);
            if (created) { setSelected(new Set()); load(); }
          }}
        />
      )}
      {assignOpen && (
        <BulkAssignModal
          clientIds={Array.from(selected)}
          managers={managers}
          onClose={(assigned) => {
            setAssignOpen(false);
            if (assigned) { setSelected(new Set()); load(); }
          }}
        />
      )}

    </div>
  );
}

/**
 * Массовое назначение ответственного менеджера на выделенные лиды.
 * Режимы: одному менеджеру или распределить по кругу между несколькими.
 */
function BulkAssignModal({ clientIds, managers, onClose }) {
  const [mode, setMode] = useState("single");       // single | round_robin
  const [singleId, setSingleId] = useState(managers[0]?.id || "");
  const [picked, setPicked] = useState(new Set());
  const [reason, setReason] = useState("admin_override");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const togglePick = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const submit = async () => {
    const manager_ids = mode === "single"
      ? (singleId ? [singleId] : [])
      : Array.from(picked);
    if (manager_ids.length === 0) {
      setError(mode === "single" ? "Выберите менеджера"
                                 : "Отметьте хотя бы двух менеджеров");
      return;
    }
    setSaving(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/crm/clients/bulk-assign`,
        { client_ids: clientIds, manager_ids, reason, note },
        { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось назначить");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="crm-bulk-assign-modal" onClick={() => onClose(!!result)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 130,
                display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: 22, width: "100%",
                  maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
                  fontFamily: "Manrope" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                       alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, color: "#0A0A0A" }}>
            Назначить ответственного · {clientIds.length} лид(ов)
          </div>
          <button onClick={() => onClose(!!result)}
            style={{ background: "none", border: "none", cursor: "pointer" }}>
            <X size={16} />
          </button>
        </div>

        {result ? (
          <div data-testid="crm-bulk-assign-result">
            <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0",
                           color: "#065F46", borderRadius: 8, padding: 14, fontSize: 13 }}>
              Назначено лидов: <b>{result.assigned}</b>
              <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
                {Object.entries(result.per_manager || {}).map(([n, cnt]) => (
                  <li key={n}>{n} — {cnt}</li>
                ))}
              </ul>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button data-testid="crm-bulk-assign-done"
                onClick={() => onClose(true)} style={btnPrimary}>Готово</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <button data-testid="assign-mode-single" onClick={() => setMode("single")}
                style={{ ..._bulkChip, ...(mode === "single" ? _bulkChipActive : {}) }}>
                Одному менеджеру
              </button>
              <button data-testid="assign-mode-rr" onClick={() => setMode("round_robin")}
                style={{ ..._bulkChip, ...(mode === "round_robin" ? _bulkChipActive : {}) }}>
                Распределить по кругу
              </button>
            </div>

            {mode === "single" ? (
              <>
                <label style={_bulkLbl}>Менеджер</label>
                <select data-testid="assign-single-select" value={singleId}
                  onChange={(e) => setSingleId(e.target.value)} style={_bulkInput}>
                  <option value="">— выберите —</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </>
            ) : (
              <>
                <label style={_bulkLbl}>
                  Между кем распределяем (отметьте 2+)
                </label>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 8,
                               maxHeight: 210, overflowY: "auto" }}>
                  {managers.map(m => (
                    <div key={m.id} data-testid={`assign-rr-${m.id}`}
                      onClick={() => togglePick(m.id)}
                      style={{ display: "flex", alignItems: "center", gap: 10,
                                padding: "10px 14px", cursor: "pointer",
                                borderBottom: "1px solid #f5f5f5",
                                background: picked.has(m.id) ? "rgba(197,160,89,0.10)" : "white" }}>
                      <input type="checkbox" checked={picked.has(m.id)}
                        onChange={() => togglePick(m.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 13 }}>{m.name}</span>
                      <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: "auto" }}>
                        {m.role === "admin" ? "админ"
                          : m.role === "senior_manager" ? "сеньор" : "менеджер"}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 6 }}>
                  Лиды раздадутся по очереди: 1-й → первому, 2-й → второму и так по кругу.
                </div>
              </>
            )}

            <label style={{ ..._bulkLbl, marginTop: 14 }}>Причина</label>
            <select data-testid="assign-reason" value={reason}
              onChange={(e) => setReason(e.target.value)} style={_bulkInput}>
              <option value="admin_override">Передача администратором</option>
              <option value="overloaded">Менеджер перегружен</option>
              <option value="shift_end">Конец смены</option>
              <option value="no_response">Не отвечает / SLA</option>
              <option value="client_request">Просьба клиента</option>
              <option value="other">Другое</option>
            </select>

            <label style={{ ..._bulkLbl, marginTop: 14 }}>Комментарий (в историю лида)</label>
            <input data-testid="assign-note" value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Например: новые заявки с рекламы"
              style={_bulkInput} />

            {error && (
              <div data-testid="assign-error"
                style={{ background: "#FEF2F2", border: "1px solid #FECACA",
                          color: "#991B1B", borderRadius: 6, padding: "8px 12px",
                          fontSize: 12.5, marginTop: 12 }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
              <button data-testid="assign-submit" onClick={submit} disabled={saving}
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Назначаем…" : `Назначить (${clientIds.length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// Small inline dropdown for the "Закрыл сделку" column. Ownership rules
// live here so the JSX above stays readable.
function CloserCell({ client, meta, managers, isPrivileged, onSaved }) {
  const canEdit = isPrivileged || client.crm_manager_id === meta.user_id;
  const closerName = managers.find(m => m.id === client.crm_closer_id)?.name;
  if (!canEdit) {
    return <span style={{ fontSize: "12px", color: "#666" }}>
      {closerName || "—"}
    </span>;
  }
  return (
    <select data-testid={`crm-row-closer-select-${client.id}`}
      value={client.crm_closer_id || ""}
      onClick={(e) => e.stopPropagation()}
      onChange={async (e) => {
        e.stopPropagation();
        const val = e.target.value;
        try {
          await axios.patch(`${API}/admin/crm/clients/${client.id}`,
                              { crm_closer_id: val }, { withCredentials: true });
          onSaved();
        } catch (err) {
          alert("Ошибка: " + (err.response?.data?.detail || err.message));
        }
      }}
      style={{ padding: "4px 8px", borderRadius: "4px",
               border: "1px solid " + (client.crm_closer_id ? "#128C7E" : "#e5e7eb"),
               background: client.crm_closer_id ? "#EBF9F3" : "white",
               fontFamily: "Manrope", fontSize: "11px", cursor: "pointer",
               maxWidth: "140px" }}>
      <option value="">— не закрыт —</option>
      {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
    </select>
  );
}

// ============================================================================
// BULK TASK MODAL — inline "распределить обзвон/wa" flow directly from CRM
// ============================================================================

function BulkTaskModal({ clientIds, clients, managers, onClose }) {
  const [taskType, setTaskType] = useState("call");
  const [title, setTitle] = useState("Обзвонить и уточнить актуальность");
  const [comment, setComment] = useState("");
  const [dueAt, setDueAt] = useState(() => {
    const d = new Date(Date.now() + 4 * 3600_000); d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [distribution, setDistribution] = useState("round_robin");
  const [singleManagerId, setSingleManagerId] = useState("");
  const [maxLoad, setMaxLoad] = useState(20);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [loads, setLoads] = useState([]);   // manager load snapshot

  useEffect(() => {
    axios.get(`${API}/admin/tasks-bulk/managers-load`, { withCredentials: true })
      .then(r => setLoads(r.data.managers || []))
      .catch(() => {});
  }, []);

  const TASK_TYPE_LABELS = {
    call: "Позвонить",
    whatsapp_reply: "Написать в WhatsApp",
    custom: "Пригласить в бутик / другое",
  };
  const TASK_DEFAULT_TITLE = {
    call: "Обзвонить и уточнить актуальность",
    whatsapp_reply: "Написать в WhatsApp и предложить подборку",
    custom: "Пригласить в бутик",
  };

  const changeType = (t) => {
    setTaskType(t);
    if (Object.values(TASK_DEFAULT_TITLE).includes(title)) {
      setTitle(TASK_DEFAULT_TITLE[t]);
    }
  };

  const submit = async () => {
    setCreating(true); setErr("");
    try {
      const payload = {
        client_ids: clientIds,
        task_type: taskType,
        title,
        comment,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        distribution,
        max_load_per_manager: Number(maxLoad) || 20,
      };
      if (distribution === "single") {
        if (!singleManagerId) { setErr("Выберите менеджера"); setCreating(false); return; }
        payload.assigned_manager_id = singleManagerId;
      }
      const r = await axios.post(`${API}/admin/tasks-bulk/create`, payload,
        { withCredentials: true });
      alert(`Готово! Создано задач: ${r.data.created_count}\n` +
            `Пропущено (дубли): ${r.data.skipped_existing_pending}\n\n` +
            r.data.distribution.map(d => `• ${d.manager_name}: ${d.assigned_count}`).join("\n"));
      onClose(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Не удалось создать задачи");
    } finally { setCreating(false); }
  };

  return (
    <div data-testid="crm-bulk-task-modal"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
                zIndex: 200, display: "flex", alignItems: "center",
                justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: 24,
                  maxWidth: 620, width: "100%", maxHeight: "90vh",
                  overflowY: "auto", fontFamily: "Manrope" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#0A0A0A" }}>
              Массовая задача · {clientIds.length} клиентов
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {clients.slice(0, 3).map(c => c.name || c.phone).join(", ")}
              {clients.length > 3 && ` +${clients.length - 3}`}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "transparent", border: "none",
                      cursor: "pointer", color: "#6b7280" }}>
            <X size={18} />
          </button>
        </div>

        {/* Task type */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {Object.entries(TASK_TYPE_LABELS).map(([k, label]) => (
            <button key={k}
              data-testid={`bulk-type-${k}`}
              onClick={() => changeType(k)}
              style={{ padding: "8px 14px",
                        border: taskType === k ? "1px solid #C5A059" : "1px solid #e5e7eb",
                        background: taskType === k ? "rgba(197,160,89,0.08)" : "white",
                        color: taskType === k ? "#0A0A0A" : "#6b7280",
                        borderRadius: 6, fontSize: 12.5, cursor: "pointer",
                        fontFamily: "Manrope" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Fields */}
        <label style={_bulkLbl}>Заголовок задачи</label>
        <input data-testid="bulk-title-input"
          value={title} onChange={(e) => setTitle(e.target.value)}
          style={_bulkInput} />

        <label style={_bulkLbl}>Комментарий / скрипт</label>
        <textarea data-testid="bulk-comment-input"
          value={comment} onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Например: спросите про размер / предложите примерку"
          style={{ ..._bulkInput, resize: "vertical" }} />

        <label style={_bulkLbl}>Срок выполнения</label>
        <input data-testid="bulk-due-input" type="datetime-local"
          value={dueAt} onChange={(e) => setDueAt(e.target.value)}
          style={{ ..._bulkInput, maxWidth: 260 }} />

        {/* Distribution */}
        <div style={{ marginTop: 14, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button data-testid="bulk-dist-rr"
              onClick={() => setDistribution("round_robin")}
              style={{ ..._bulkChip, ...(distribution === "round_robin" ? _bulkChipActive : {}) }}>
              Round-robin (авто, пропустить перегруженных)
            </button>
            <button data-testid="bulk-dist-single"
              onClick={() => setDistribution("single")}
              style={{ ..._bulkChip, ...(distribution === "single" ? _bulkChipActive : {}) }}>
              Одному менеджеру
            </button>
          </div>

          {distribution === "single" ? (
            <>
              <label style={_bulkLbl}>Менеджер</label>
              <select data-testid="bulk-single-mgr"
                value={singleManagerId}
                onChange={(e) => setSingleManagerId(e.target.value)}
                style={_bulkInput}>
                <option value="">— Выберите —</option>
                {loads.length > 0 ? loads.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} {m.online ? "🟢" : "⚪"} · {m.pending_tasks} в работе
                  </option>
                )) : managers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label style={_bulkLbl}>Пропускать менеджеров с ≥ N открытыми задачами</label>
              <input data-testid="bulk-max-load" type="number" min="1"
                value={maxLoad} onChange={(e) => setMaxLoad(e.target.value)}
                style={{ ..._bulkInput, maxWidth: 140 }} />
              {loads.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: "#6b7280" }}>
                  Онлайн сейчас: {loads.filter(m => m.online).length} · попадут в лимит:{" "}
                  <b>{loads.filter(m => m.online && m.pending_tasks < Number(maxLoad || 20)).length}</b>
                </div>
              )}
            </>
          )}
        </div>

        {err && (
          <div data-testid="bulk-error"
            style={{ marginTop: 12, padding: 10, background: "#fef2f2",
                      border: "1px solid #fecaca", color: "#991b1b",
                      borderRadius: 6, fontSize: 12.5 }}>
            {err}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end",
                       gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={btnSecondary}>Отмена</button>
          <button data-testid="bulk-submit"
            onClick={submit} disabled={creating}
            style={{ ...btnPrimary, opacity: creating ? 0.5 : 1 }}>
            <Check size={13} /> {creating ? "Создаём…" : "Распределить задачи"}
          </button>
        </div>
      </div>
    </div>
  );
}
const _bulkLbl = { display: "block", fontSize: 11, textTransform: "uppercase",
                    letterSpacing: "0.12em", color: "#8a8a89", marginBottom: 4,
                    marginTop: 10, fontWeight: 700 };
const _bulkInput = { width: "100%", padding: "10px 12px", borderRadius: 6,
                      border: "1px solid #e2e0d7", fontSize: 13,
                      fontFamily: "Manrope", background: "white",
                      color: "#0A0A0A", boxSizing: "border-box" };
const _bulkChip = { padding: "8px 14px", background: "white",
                     border: "1px solid #e5e7eb", borderRadius: 6,
                     cursor: "pointer", fontSize: 12, color: "#6b7280",
                     fontFamily: "Manrope", flex: 1 };
const _bulkChipActive = { border: "1px solid #C5A059",
                           background: "rgba(197,160,89,0.08)", color: "#0A0A0A" };




// ============================================================================
// ASSIGNMENT BANNER + REASSIGN MODAL
// ============================================================================

function AssignmentBanner({ client, meta, managers, onChange }) {
  const [showReassign, setShowReassign] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sla, setSla] = useState(null);
  const owner = managers.find((m) => m.id === client.crm_manager_id);
  const isOwner = client.crm_manager_id === meta.user_id;
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);
  const canReassign = isPrivileged || isOwner;

  // Live-refresh the SLA countdown every 10 seconds while the card is open.
  useEffect(() => {
    let alive = true;
    const compute = () => {
      if (!client.lead_received_at || client.first_response_at) {
        setSla(client.first_response_at ? { status: "ok" } : null);
        return;
      }
      const anchor = new Date(client.lead_received_at).getTime();
      const elapsed = (Date.now() - anchor) / 1000;
      const slaMin = 5, reassignMin = 10;   // client-side visual — server owns truth
      const slaSec = slaMin * 60, reassignSec = reassignMin * 60;
      const status = elapsed >= reassignSec ? "breached"
                   : elapsed >= slaSec ? "warn" : "ok";
      const leftSec = Math.max(0, slaSec - elapsed);
      setSla({ status, seconds_left: Math.round(leftSec) });
    };
    compute();
    const t = setInterval(() => { if (alive) compute(); }, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [client.lead_received_at, client.first_response_at]);

  const requestTransfer = async () => {
    const target = window.prompt("Запросить передачу — напишите короткую причину:");
    if (!target) return;
    await axios.post(`${API}/admin/crm/clients/${client.id}/notes`,
      { text: `🔁 Запрос передачи клиента от ${meta.name || meta.email}: ${target}` },
      { withCredentials: true });
    alert("Заявка сохранена в заметках. Админ увидит и передаст клиента.");
  };

  if (!client.crm_manager_id) {
    return (
      <div data-testid="assignment-banner-unassigned"
        style={{ padding: "10px 14px", borderRadius: 8,
                  background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.35)",
                  color: "#991b1b", fontFamily: "Manrope", fontSize: 12.5,
                  marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <AlertCircle size={16} />
        <span style={{ flex: 1 }}>Лид без ответственного менеджера</span>
        {isPrivileged && (
          <button data-testid="reassign-btn"
            onClick={() => setShowReassign(true)}
            style={{ ...btnPrimary, padding: "6px 14px" }}>
            Назначить менеджера
          </button>
        )}
        {showReassign && (
          <ReassignModal client={client} managers={managers}
            onClose={(saved) => { setShowReassign(false); if (saved) onChange(); }} />
        )}
      </div>
    );
  }

  return (
    <div data-testid="assignment-banner"
      style={{ padding: "10px 14px", borderRadius: 8,
                background: isOwner ? "rgba(16,185,129,0.06)" : "rgba(197,160,89,0.06)",
                border: isOwner ? "1px solid rgba(16,185,129,0.35)" : "1px solid rgba(197,160,89,0.35)",
                fontFamily: "Manrope", fontSize: 12.5,
                marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
        <span style={{ background: isOwner ? "#10B981" : "#C5A059", color: "white",
                       padding: "2px 10px", borderRadius: 999, fontSize: 10.5,
                       fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {isOwner ? "Ваш клиент" : "Ведёт " + (owner?.name || "менеджер")}
        </span>
        {sla && sla.status && sla.status !== "ok" && sla.status !== "na" && (
          <span data-testid="sla-badge"
            style={{ background: sla.status === "breached" ? "#DC2626" : "#F59E0B",
                     color: "white", padding: "2px 10px", borderRadius: 999,
                     fontSize: 10.5, fontWeight: 800, letterSpacing: "0.05em",
                     animation: sla.status === "breached" ? "slaPulse 1.4s ease-in-out infinite" : "none" }}>
            {sla.status === "breached" ? "🔴 SLA нарушен" : "⚠️ Скоро SLA"}
          </span>
        )}
        {sla && sla.status === "ok" && sla.seconds_left != null && (
          <span data-testid="sla-countdown"
            style={{ color: "#065F46", fontSize: 11, fontWeight: 600 }}>
            SLA: {Math.floor(sla.seconds_left / 60)}м {sla.seconds_left % 60}с
          </span>
        )}
        <style>{`@keyframes slaPulse {0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
        {client.assignment_history_count > 0 && (
          <button data-testid="assignment-history-btn"
            onClick={() => setShowHistory(!showHistory)}
            style={{ background: "transparent", border: "none", cursor: "pointer",
                      color: "#6b7280", fontSize: 11, textDecoration: "underline" }}>
            История передач ({client.assignment_history_count})
          </button>
        )}
      </div>
      {canReassign && (
        <button data-testid="reassign-btn"
          onClick={() => setShowReassign(true)}
          style={btnSecondary}>
          {isPrivileged ? "Переназначить" : "Передать клиента"}
        </button>
      )}
      {!isOwner && !isPrivileged && (
        <button data-testid="request-transfer-btn"
          onClick={requestTransfer} style={btnSecondary}>
          Запросить передачу
        </button>
      )}
      {showHistory && <AssignmentHistoryPopover clientId={client.id} onClose={() => setShowHistory(false)} />}
      {showReassign && (
        <ReassignModal client={client} managers={managers}
          onClose={(saved) => { setShowReassign(false); if (saved) onChange(); }} />
      )}
    </div>
  );
}

function AssignmentHistoryPopover({ clientId, onClose }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/crm/clients/${clientId}/assignment-history`,
              { withCredentials: true })
      .then(r => setItems(r.data.items || []))
      .catch(() => {});
  }, [clientId]);
  return (
    <div data-testid="assignment-history-popover"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
                zIndex: 210, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", padding: 20, borderRadius: 10,
                  maxWidth: 460, width: "100%", maxHeight: "70vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
          <b style={{ fontFamily: "Manrope" }}>История передач</b>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={16}/></button>
        </div>
        {items.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 12, textAlign: "center", padding: 20 }}>Пока пусто</div>
        ) : items.map((h, i) => (
          <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9", fontSize: 12.5, fontFamily: "Manrope" }}>
            <div>
              <b style={{ color: "#0A0A0A" }}>{h.from_name || "—"}</b>{" → "}
              <b style={{ color: "#0A0A0A" }}>{h.to_name || "снят"}</b>
            </div>
            <div style={{ color: "#6b7280", fontSize: 11 }}>
              {new Date(h.at).toLocaleString("ru-RU")} · причина: {h.reason} · {h.by_name || "система"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReassignModal({ client, managers, onClose }) {
  const [toManager, setToManager] = useState("");
  const [reason, setReason] = useState("shift_end");
  const [note, setNote] = useState("");
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/crm/clients/${client.id}/assignment-history`,
              { withCredentials: true })
      .then(r => setReasons(r.data.reasons || []))
      .catch(() => {});
  }, [client.id]);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/crm/clients/${client.id}/reassign`,
        { to_manager_id: toManager, reason, note },
        { withCredentials: true });
      onClose(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось передать клиента");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="reassign-modal"
      onClick={() => onClose(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", padding: 22, borderRadius: 12,
                  maxWidth: 460, width: "100%", fontFamily: "Manrope" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 20 }}>
            Передать клиента
          </div>
          <button onClick={() => onClose(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>
        <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#8a8a89", fontWeight: 700, marginBottom: 4 }}>Кому передать</label>
        <select data-testid="reassign-target"
          value={toManager}
          onChange={(e) => setToManager(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #e2e0d7", fontSize: 13 }}>
          <option value="">— Снять (в общий пул) —</option>
          {managers.map(m => (
            <option key={m.id} value={m.id}>{m.name} · {m.role}</option>
          ))}
        </select>
        <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#8a8a89", fontWeight: 700, marginTop: 12, marginBottom: 4 }}>Причина</label>
        <select data-testid="reassign-reason"
          value={reason} onChange={(e) => setReason(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #e2e0d7", fontSize: 13 }}>
          {reasons.length ? reasons.map(r => (
            <option key={r.key} value={r.key}>{r.label}</option>
          )) : (
            <>
              <option value="shift_end">Конец смены</option>
              <option value="client_request">Клиент попросил другого</option>
              <option value="overloaded">Менеджер перегружен</option>
              <option value="admin_override">Передача администратором</option>
              <option value="no_response">Не отвечает / SLA</option>
              <option value="other">Другое</option>
            </>
          )}
        </select>
        <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#8a8a89", fontWeight: 700, marginTop: 12, marginBottom: 4 }}>Комментарий (необязательно)</label>
        <textarea data-testid="reassign-note"
          value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          placeholder="Что важно знать новому менеджеру"
          style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #e2e0d7", fontSize: 13, resize: "vertical" }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
          <button data-testid="reassign-submit" onClick={submit} disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
            {saving ? "Передаём…" : "Передать"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// CLIENT DETAIL (slide-in panel)
// ============================================================================

function ClientDetail({ clientId, meta, managers, onClose }) {
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [newNote, setNewNote] = useState("");
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showClaim, setShowClaim] = useState(false);
  const [chatDirty, setChatDirty] = useState(false);
  const [askCloser, setAskCloser] = useState(null); // holds pending status when modal open
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);

  // Клик по затемнению больше НЕ закрывает карточку: на телефоне тап рядом с
  // полем ввода (или «прыжок» вёрстки при появлении клавиатуры) выбрасывал
  // менеджера из лида прямо во время набора ответа. Закрыть можно кнопкой ✕
  // или Escape — и то с подтверждением, если ответ не отправлен.
  const requestClose = () => {
    if (chatDirty) {
      if (!window.confirm("У вас есть несохранённый ответ в WhatsApp. Покинуть чат?"))
        return;
    }
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatDirty]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/crm/clients/${clientId}`, { withCredentials: true });
      setC(r.data);
      setEdit({
        name: r.data.name || "",
        city: r.data.city || "",
        crm_size: r.data.crm_size || "",
        crm_interested_in: r.data.crm_interested_in || "",
        whatsapp: r.data.whatsapp || r.data.phone || "",
      });
    } catch (e) {
      // A stale row (e.g. auto-reassigned by SLA cron before the manager
      // could click) yields 403 from /admin/crm/clients/{id}. Rather than
      // leave a broken modal open, show a friendly notice and close.
      const msg = e.response?.status === 403
        ? "Этот клиент уже передан другому менеджеру"
        : (e.response?.data?.detail || "Не удалось загрузить клиента");
      alert(msg);
      onClose();
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [clientId]);

  if (!c) return null;

  const save = async () => {
    try {
      await axios.patch(`${API}/admin/crm/clients/${clientId}`, edit, { withCredentials: true });
      setEditing(false);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось сохранить изменения");
    }
  };
  const changeStatus = async (newStatus) => {
    // Auto-prompt: switching a client to "Купил" without a closer would
    // lose commission attribution. Force the manager to pick the offline
    // salesperson before we apply the status change.
    if (newStatus === "purchased" && !c.crm_closer_id) {
      setAskCloser(newStatus);
      return;
    }
    await axios.patch(`${API}/admin/crm/clients/${clientId}`, { crm_status: newStatus }, { withCredentials: true });
    refresh();
  };
  const addNote = async () => {
    if (!newNote.trim()) return;
    await axios.post(`${API}/admin/crm/clients/${clientId}/notes`, { text: newNote }, { withCredentials: true });
    setNewNote("");
    refresh();
  };

  return (
    <div className="crm-client-detail-shell"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", justifyContent: "flex-end" }}>
      <div data-testid="crm-client-detail" className="crm-client-detail" onClick={(e) => e.stopPropagation()}
           style={{ background: "white", width: "100%", maxWidth: "640px", height: "100vh", overflow: "auto",
                    boxShadow: "-8px 0 32px rgba(0,0,0,0.15)" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      position: "sticky", top: 0, background: "white", zIndex: 5 }}>
          <div style={{ flex: 1 }}>
            {editing ? (
              <input data-testid="crm-detail-name-input"
                     value={edit.name}
                     onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                     placeholder="Имя клиента"
                     style={{ fontFamily: "Cormorant Garamond", fontSize: "22px",
                              fontWeight: 400, width: "100%",
                              border: "1px solid #d1d5db", borderRadius: "6px",
                              padding: "4px 10px", color: "#0A0A0A",
                              background: "white", boxSizing: "border-box" }} />
            ) : (
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400 }}>{c.name || "— без имени —"}</div>
            )}
            <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#999" }}>{c.phone}</div>
          </div>
          <button data-testid="crm-detail-close" onClick={requestClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {/* Assignment banner: shows current owner + reassign / transfer request */}
          <AssignmentBanner client={c} meta={meta} managers={managers} onChange={refresh} />

          {/* Status changer */}
          <SectionTitle>Статус в воронке</SectionTitle>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "20px" }}>
            {meta.statuses.map(s => (
              <button key={s.key} data-testid={`crm-status-pill-${s.key}`} onClick={() => changeStatus(s.key)}
                      style={{ padding: "6px 12px", border: "none", borderRadius: "20px",
                               background: c.crm_status === s.key ? s.color : "#f1f5f9",
                               color: c.crm_status === s.key ? "white" : "#666",
                               fontFamily: "Manrope", fontSize: "11px", fontWeight: 600,
                               cursor: "pointer", letterSpacing: "0.02em" }}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Quick actions */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
            <a data-testid="crm-detail-wa-btn" href={waLink(c.whatsapp || c.phone, { text: WA_GREETING })} target="_blank" rel="noreferrer"
               style={{ ...btnPrimary, background: "#25D366", textDecoration: "none" }}>
              <MessageCircle size={14} /> WhatsApp
            </a>
            <a data-testid="crm-detail-call-btn" href={waCallLink(c.whatsapp || c.phone)} target="_blank" rel="noreferrer"
               title="Позвонить через WhatsApp" style={btnSecondary}>
              <Phone size={14} /> Позвонить
            </a>
            <button data-testid="crm-detail-add-task" onClick={() => setShowTaskForm(true)} style={btnSecondary}>
              <Plus size={14} /> Задача
            </button>
            <button onClick={() => setEditing(!editing)} style={btnSecondary}>
              {editing ? "Отмена" : "Редактировать"}
            </button>
          </div>

          {/* WhatsApp переписка — главный рабочий инструмент менеджера */}
          <SectionTitle>WhatsApp</SectionTitle>
          <div style={{ marginBottom: "24px" }}>
            <WhatsAppChat clientId={clientId} client={c} onDirtyChange={setChatDirty} />
          </div>

          {/* Manager assignment */}
          {!c.crm_manager_id ? (
            <>
              <SectionTitle>Ответственный менеджер</SectionTitle>
              <div data-testid="crm-detail-free-block"
                   style={{ background: "#ECFDF5", border: "1px solid #A7F3D0",
                            borderRadius: "8px", padding: "12px 14px", marginBottom: "20px" }}>
                <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "#065F46", lineHeight: 1.5, marginBottom: "10px" }}>
                  <b>Свободный клиент.</b> Свяжитесь с ним, выясните потребность — и возьмите
                  в работу, заполнив результат первого контакта и следующий шаг.
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <button data-testid="crm-detail-claim-btn" onClick={() => setShowClaim(true)}
                          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                                   border: "none", borderRadius: "5px", padding: "9px 16px", cursor: "pointer",
                                   fontFamily: "Manrope", fontSize: "11.5px", fontWeight: 700,
                                   letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Взять клиента
                  </button>
                  {isPrivileged && (
                    <select data-testid="crm-detail-manager-select"
                            value=""
                            onChange={async (e) => {
                              const val = e.target.value;
                              if (!val) return;
                              try {
                                await axios.patch(`${API}/admin/crm/clients/${clientId}`,
                                                  { crm_manager_id: val }, { withCredentials: true });
                                refresh();
                              } catch (err) {
                                alert(err.response?.data?.detail || "Ошибка назначения");
                              }
                            }}
                            style={{ ...selStyle }}>
                      <option value="">Назначить другого менеджера…</option>
                      {managers.filter(m => m.id !== meta.user_id).map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                    </select>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <SectionTitle>Ответственный менеджер</SectionTitle>
              <div data-testid="crm-detail-manager-assign"
                   style={{ background: "#fafaf9", border: "1px solid #f0e7d8",
                            borderRadius: "8px", padding: "10px 12px", marginBottom: "20px",
                            display: "flex", alignItems: "center", gap: "8px" }}>
                {isPrivileged ? (
                  <select data-testid="crm-detail-manager-select"
                          value={c.crm_manager_id || ""}
                          onChange={async (e) => {
                            const val = e.target.value;
                            try {
                              await axios.patch(`${API}/admin/crm/clients/${clientId}`,
                                                { crm_manager_id: val }, { withCredentials: true });
                              refresh();
                            } catch (err) {
                              alert(err.response?.data?.detail || "Ошибка назначения");
                            }
                          }}
                          style={{ ...selStyle, flex: 1 }}>
                    <option value="">— Снять (в общий пул) —</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                  </select>
                ) : (
                  <span style={{ flex: 1, fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A", fontWeight: 600 }}>
                    {c.crm_manager_id === meta.user_id
                      ? "Вы"
                      : (managers.find(m => m.id === c.crm_manager_id)?.name || "Другой менеджер")}
                  </span>
                )}
                <span style={{ display: "flex", alignItems: "center", gap: "4px",
                                color: "#15803d", fontFamily: "Manrope", fontSize: "11px", fontWeight: 600 }}>
                  <Check size={12} /> Назначен
                </span>
              </div>
            </>
          )}

          {/* Closer — offline salesperson who received cash */}
          <SectionTitle>Закрыл сделку (оффлайн-продавец)</SectionTitle>
          <div style={{ marginBottom: "24px", padding: "12px 14px",
                         background: c.crm_closer_id ? "#EBF9F3" : "#fafaf9",
                         border: "1px solid " + (c.crm_closer_id ? "#BEE3D5" : "#e5e7eb"),
                         borderRadius: "8px" }}>
            {(isPrivileged || c.crm_manager_id === meta.user_id) ? (
              <>
                <select data-testid="crm-detail-closer-select"
                  value={c.crm_closer_id || ""}
                  onChange={async (e) => {
                    try {
                      await axios.patch(`${API}/admin/crm/clients/${c.id}`,
                        { crm_closer_id: e.target.value }, { withCredentials: true });
                      refresh();
                    } catch (err) {
                      alert("Ошибка: " + (err.response?.data?.detail || err.message));
                    }
                  }}
                  style={{ padding: "8px 12px", borderRadius: "6px",
                           border: "1px solid " + (c.crm_closer_id ? "#128C7E" : "#e5e7eb"),
                           background: "white", fontFamily: "Manrope", fontSize: "13px",
                           cursor: "pointer", width: "100%", maxWidth: "320px" }}>
                  <option value="">— ещё не закрыто —</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <div style={{ marginTop: 6, fontFamily: "Manrope", fontSize: 11, color: "#6b7280" }}>
                  Онлайн-менеджер получает % за ведение лида,
                  оффлайн-продавец — за закрытие в бутике.
                </div>
              </>
            ) : (
              <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#0A0A0A" }}>
                {managers.find(m => m.id === c.crm_closer_id)?.name || "— ещё не закрыто —"}
              </div>
            )}
          </div>

          {/* Boutique manager — offline salesperson expected to receive
              this client at fitting. Separate from closer (only stamped
              on purchase) and from online manager (owns the online lead). */}
          <SectionTitle>Менеджер бутика (примерка)</SectionTitle>
          <div style={{ marginBottom: "24px", padding: "12px 14px",
                         background: c.boutique_manager_id ? "rgba(197,160,89,0.08)" : "#fafaf9",
                         border: "1px solid " + (c.boutique_manager_id ? "#C5A059" : "#e5e7eb"),
                         borderRadius: "8px" }}>
            {(isPrivileged || c.crm_manager_id === meta.user_id) ? (
              <>
                <select data-testid="crm-detail-boutique-select"
                  value={c.boutique_manager_id || ""}
                  onChange={async (e) => {
                    try {
                      await axios.patch(`${API}/admin/crm/clients/${c.id}`,
                        { boutique_manager_id: e.target.value }, { withCredentials: true });
                      refresh();
                    } catch (err) {
                      alert("Ошибка: " + (err.response?.data?.detail || err.message));
                    }
                  }}
                  style={{ padding: "8px 12px", borderRadius: "6px",
                           border: "1px solid " + (c.boutique_manager_id ? "#C5A059" : "#e5e7eb"),
                           background: "white", fontFamily: "Manrope", fontSize: "13px",
                           cursor: "pointer", width: "100%", maxWidth: "320px" }}>
                  <option value="">— не назначен —</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <div style={{ marginTop: 6, fontFamily: "Manrope", fontSize: 11, color: "#6b7280" }}>
                  Кто встретит клиента на примерке. При записи в бутик — обязательно назначить.
                </div>
              </>
            ) : (
              <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#0A0A0A" }}>
                {managers.find(m => m.id === c.boutique_manager_id)?.name || "— не назначен —"}
              </div>
            )}
          </div>

          {/* Info grid */}
          <SectionTitle>Информация</SectionTitle>
          <div className="crm-info-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "24px" }}>
            <Info label="Город">
              {editing ? <Inp data-testid="crm-detail-city-input" value={edit.city} onChange={(v) => setEdit({ ...edit, city: v })} placeholder="Алматы, Астана…" /> : (c.city || "—")}
            </Info>
            <Info label="WhatsApp">
              {editing ? <Inp value={edit.whatsapp} onChange={(v) => setEdit({ ...edit, whatsapp: v })} /> : (c.whatsapp || c.phone)}
            </Info>
            <Info label="Размер">
              {editing ? <Inp value={edit.crm_size} onChange={(v) => setEdit({ ...edit, crm_size: v })} placeholder="M / 42 / XL" /> : (c.crm_size || "—")}
            </Info>
            <Info label="Интересующий товар" colSpan={2}>
              {editing ? <Inp value={edit.crm_interested_in} onChange={(v) => setEdit({ ...edit, crm_interested_in: v })} placeholder="Пальто, шуба…" /> : (c.crm_interested_in || "—")}
            </Info>
            <Info label="Бонусы">{fmtMoney(c.bonus_balance)}</Info>
            <Info label="Покупок">{fmtMoney(c.total_purchases)} ({c.vip_level})</Info>
            <Info label="Дата рождения">{c.birth_date || "—"}</Info>
            <Info label="Источник">{c.crm_source || "—"}</Info>
            <Info label="Виртуальных примерок">
              <strong style={{ color: c.tryon_history?.length ? "#C5A059" : "#999" }}>
                {c.tryon_history?.length || 0}
              </strong>
              {c.tryon_history?.length ? " шт." : ""}
            </Info>
            <Info label="Приглашённых друзей">
              <strong style={{ color: c.referrals_count ? "#10b981" : "#999" }}>
                {c.referrals_count || 0}
              </strong>
              {c.referral_code ? (
                <span style={{ fontSize: "11px", color: "#888", marginLeft: "6px" }}>
                  · код <code style={{ background: "#f1f5f9", padding: "1px 5px", borderRadius: "3px", fontSize: "11px" }}>{c.referral_code}</code>
                </span>
              ) : null}
            </Info>
          </div>

          {editing && (
            <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
              <button data-testid="crm-detail-save" onClick={save} style={btnPrimary}><Check size={14} /> Сохранить</button>
              <button onClick={() => setEditing(false)} style={btnSecondary}>Отмена</button>
            </div>
          )}

          {/* Лид из рекламы Meta: ИИ-скоринг готовности + ответы лид-формы */}
          {(c.lead_score != null || c.fb_ad_name || (c.fb_answers || []).length > 0) && (
            <div data-testid="crm-detail-ad-lead" style={{ background: "#FFFBEB",
                  border: "1px solid #FDE68A", borderRadius: "8px", padding: "12px 14px",
                  marginBottom: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 800,
                                textTransform: "uppercase", letterSpacing: "0.08em",
                                color: "#92400e" }}>Лид из рекламы</span>
                {c.lead_score != null && (
                  <span data-testid="crm-detail-lead-score" style={{ fontFamily: "Manrope",
                        fontSize: "13px", fontWeight: 800,
                        color: c.lead_readiness === "hot" ? "#dc2626"
                             : c.lead_readiness === "warm" ? "#d97706" : "#64748b" }}>
                    {c.lead_score}/100 · {c.lead_readiness === "hot" ? "горячий"
                      : c.lead_readiness === "warm" ? "тёплый" : "холодный"}
                  </span>
                )}
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "#78350f",
                             marginTop: "6px", lineHeight: 1.5 }}>
                {[c.fb_campaign_name, c.fb_ad_name || c.fb_form_name].filter(Boolean).join(" · ") || "—"}
                {c.fb_ready_when ? ` · готов приехать: ${c.fb_ready_when}` : ""}
              </div>
              {c.lead_score_reason && (
                <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "#0A0A0A",
                               marginTop: "6px", lineHeight: 1.5 }}>{c.lead_score_reason}</div>
              )}
              {c.lead_next_action && (
                <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "#065f46",
                               marginTop: "4px", lineHeight: 1.5 }}>
                  Что делать: {c.lead_next_action}
                </div>
              )}
              {(c.fb_answers || []).length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {c.fb_answers.map((a, i) => (
                    <div key={i} style={{ fontFamily: "Manrope", fontSize: "12.5px", padding: "1px 0" }}>
                      <span style={{ color: "#8a8a89" }}>{a.name}:</span> <strong>{a.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Try-on history — visible to managers as a "hot lead" signal */}
          {c.tryon_history?.length > 0 && (
            <div data-testid="crm-detail-tryons">
              <SectionTitle>Виртуальные примерки ({c.tryon_history.length})</SectionTitle>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(100px,1fr))",
                            gap: "8px", marginBottom: "20px" }}>
                {c.tryon_history.slice(0, 12).map(tr => (
                  <a key={tr.id} href={tr.url} target="_blank" rel="noreferrer"
                     data-testid={`crm-tryon-${tr.id}`}
                     style={{ position: "relative", aspectRatio: "3/4", borderRadius: "6px",
                              overflow: "hidden", background: "#0A0A0A",
                              border: "1px solid #e5e7eb", textDecoration: "none" }}>
                    <img src={tr.url} alt={tr.product_name || ""}
                         loading="lazy"
                         style={{ width: "100%", height: "100%", objectFit: "cover" }}
                         onError={e => { e.target.style.opacity = "0.2"; }} />
                    <div style={{ position: "absolute", inset: 0,
                                  background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.85) 100%)",
                                  pointerEvents: "none" }} />
                    <div style={{ position: "absolute", bottom: "4px", left: "6px", right: "6px",
                                  color: "white", fontFamily: "Manrope", fontSize: "10px",
                                  lineHeight: 1.2, fontWeight: 600,
                                  overflow: "hidden", display: "-webkit-box",
                                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                  textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                      {tr.product_name || "Товар"}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Referrals */}
          {c.referrals && c.referrals.length > 0 && (
            <div data-testid="crm-detail-referrals">
              <SectionTitle>Приглашённые друзья ({c.referrals_count})</SectionTitle>
              <div style={{ background: "white", border: "1px solid #e5e7eb",
                            borderRadius: "8px", marginBottom: "20px", overflow: "hidden" }}>
                {c.referrals.map((r, idx) => (
                  <div key={r.id} data-testid={`crm-referral-${r.id}`}
                       style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "10px 14px",
                                borderBottom: idx < c.referrals.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <div>
                      <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 600 }}>{r.name || "—"}</div>
                      <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#999" }}>{r.phone}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ background: VIP_COLOR[r.vip_level] || "#888", color: "#0A0A0A",
                                     padding: "2px 8px", borderRadius: "4px", fontSize: "10px",
                                     fontWeight: 700, fontFamily: "Manrope" }}>{r.vip_level}</span>
                      <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#666", marginTop: "2px" }}>
                        {fmtMoney(r.total_purchases)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks */}
          <SectionTitle>Задачи ({c.tasks?.length || 0})</SectionTitle>
          {c.tasks?.length === 0 ? (
            <div style={{ color: "#999", fontFamily: "Manrope", fontSize: "13px", padding: "12px 0" }}>Нет задач</div>
          ) : (
            <div style={{ marginBottom: "20px" }}>
              {c.tasks.slice(0, 10).map(t => <TaskRow key={t.id} task={t} onChange={refresh} meta={meta} />)}
            </div>
          )}

          {/* Notes */}
          <SectionTitle>Заметки и история ({c.notes?.length || 0})</SectionTitle>
          <div style={{ marginBottom: "12px" }}>
            <textarea data-testid="crm-note-input" value={newNote} onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Что обсудили с клиентом…"
                      style={{ width: "100%", minHeight: "60px", padding: "10px", border: "1px solid #e5e7eb",
                               borderRadius: "6px", fontFamily: "Manrope", fontSize: "13px", resize: "vertical" }} />
            <button data-testid="crm-note-add-btn" onClick={addNote} style={{ ...btnPrimary, marginTop: "8px" }}>
              <Plus size={13} /> Добавить заметку
            </button>
          </div>
          {(c.notes || []).map(n => (
            <div key={n.id} style={{ borderLeft: "2px solid #C5A059", padding: "8px 12px", marginBottom: "8px",
                                      background: "#fafaf9", borderRadius: "0 6px 6px 0" }}>
              <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A", whiteSpace: "pre-wrap" }}>{n.text}</div>
              <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#999", marginTop: "4px" }}>
                {n.manager_name || "—"} · {fmtDateTime(n.created_at)}
              </div>
            </div>
          ))}

          {/* Appointments */}
          {c.appointments?.length > 0 && (
            <>
              <SectionTitle>История примерок</SectionTitle>
              {c.appointments.map(a => (
                <div key={a.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9", fontFamily: "Manrope", fontSize: "13px" }}>
                  <div><Calendar size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: "6px", color: "#C5A059" }} />
                       {a.date} {a.time}</div>
                  <div style={{ color: "#999" }}>{a.status}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {showTaskForm && (
          <TaskForm clientId={clientId} managers={managers} meta={meta}
                    onClose={(created) => { setShowTaskForm(false); if (created) refresh(); }} />
        )}
        {showClaim && (
          <ClaimModal client={c} meta={meta}
                      onClose={(saved) => { setShowClaim(false); if (saved) refresh(); }} />
        )}
        {askCloser && (
          <CloserPromptModal client={c} managers={managers}
            onCancel={() => setAskCloser(null)}
            onConfirm={async (closerId) => {
              try {
                await axios.patch(`${API}/admin/crm/clients/${clientId}`,
                  { crm_status: askCloser, crm_closer_id: closerId }, { withCredentials: true });
                setAskCloser(null);
                refresh();
              } catch (err) {
                alert(err.response?.data?.detail || "Не удалось сохранить");
              }
            }} />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PIPELINE (kanban-style funnel view, drag-and-drop)
// ============================================================================

function PipelinePane({ meta, managers }) {
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [managerId, setManagerId] = useState("");
  const [movingId, setMovingId] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [openClientId, setOpenClientId] = useState(null);
  const [pendingCloser, setPendingCloser] = useState(null); // {client, toStatus, fromStatus, moved}
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);

  const sensors = useDndSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = async () => {
    setLoading(true);
    const results = {};
    await Promise.all(meta.statuses.map(async s => {
      const params = { status: s.key, limit: 50 };
      if (managerId) params.manager_id = managerId;
      const r = await axios.get(`${API}/admin/crm/clients`, { params, withCredentials: true });
      results[s.key] = r.data.items;
    }));
    setColumns(results);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [managerId]);

  const findClient = (id) => {
    for (const k of Object.keys(columns)) {
      const c = (columns[k] || []).find(x => x.id === id);
      if (c) return { client: c, status: k };
    }
    return null;
  };

  const handleDragStart = (e) => {
    const found = findClient(e.active.id);
    if (found) setActiveCard(found.client);
  };

  const handleDragEnd = async (e) => {
    setActiveCard(null);
    const clientId = e.active?.id;
    const overId = e.over?.id;
    if (!clientId || !overId) return;
    // overId can be a column key OR another card id. Resolve to a column.
    let toStatus = null;
    if (meta.statuses.find(s => s.key === overId)) {
      toStatus = overId;
    } else {
      const overC = findClient(overId);
      if (overC) toStatus = overC.status;
    }
    const moved = findClient(clientId);
    if (!toStatus || !moved || moved.status === toStatus) return;

    // Auto-prompt: moving into «Купил» without a closer would lose
    // commission attribution. Ask BEFORE we apply the drag.
    if (toStatus === "purchased" && !moved.client.crm_closer_id) {
      setPendingCloser({ client: moved.client, toStatus, fromStatus: moved.status });
      return;
    }

    await applyStatusMove({ clientId, toStatus, fromStatus: moved.status, moved });
  };

  const applyStatusMove = async ({ clientId, toStatus, fromStatus, moved, closerId }) => {
    // Optimistic move
    setColumns(prev => {
      const next = { ...prev };
      next[fromStatus] = (next[fromStatus] || []).filter(c => c.id !== clientId);
      const newStatusMeta = meta.statuses.find(s => s.key === toStatus);
      next[toStatus] = [{
        ...moved.client,
        crm_status: toStatus,
        crm_status_label: newStatusMeta?.label,
        crm_status_color: newStatusMeta?.color,
        ...(closerId ? { crm_closer_id: closerId } : {}),
      }, ...(next[toStatus] || [])];
      return next;
    });
    setMovingId(clientId);
    try {
      const payload = { crm_status: toStatus };
      if (closerId) payload.crm_closer_id = closerId;
      await axios.patch(`${API}/admin/crm/clients/${clientId}`,
                        payload, { withCredentials: true });
    } catch (err) {
      // Revert on failure
      setColumns(prev => {
        const next = { ...prev };
        next[toStatus] = (next[toStatus] || []).filter(c => c.id !== clientId);
        next[fromStatus] = [moved.client, ...(next[fromStatus] || [])];
        return next;
      });
      alert("Не удалось обновить статус: " + (err.response?.data?.detail || err.message));
    } finally {
      setMovingId(null);
    }
  };

  return (
    <div>
      {isPrivileged && (
        <div style={{ marginBottom: "16px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontFamily: "Manrope", fontSize: "12px", color: "#666" }}>Менеджер:</span>
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)} style={selStyle}>
            <option value="">Все</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "#999", marginLeft: "8px" }}>
            💡 Перетаскивайте карточки между колонками для смены статуса
          </span>
        </div>
      )}
      {loading ? <Loading /> : (
        <DndContext sensors={sensors} collisionDetection={closestCorners}
                    onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="crm-kanban" style={{ display: "flex", gap: "12px", overflowX: "auto", paddingBottom: "16px" }}>
            {meta.statuses.map(s => (
              <KanbanColumn key={s.key} status={s}
                            items={columns[s.key] || []}
                            movingId={movingId}
                            onCardClick={(id) => setOpenClientId(id)} />
            ))}
          </div>
          <DragOverlay>
            {activeCard ? (
              <div style={{ background: "white", border: "1px solid #C5A059", borderRadius: "6px",
                             padding: "8px 10px", boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
                             cursor: "grabbing", width: "236px", transform: "rotate(2deg)" }}>
                <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 600 }}>{activeCard.name}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#999" }}>{activeCard.phone}</div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      {openClientId && (
        <ClientDetail clientId={openClientId} meta={meta} managers={managers}
                      onClose={() => { setOpenClientId(null); load(); }} />
      )}
      {pendingCloser && (
        <CloserPromptModal client={pendingCloser.client} managers={managers}
          onCancel={() => setPendingCloser(null)}
          onConfirm={async (closerId) => {
            const p = pendingCloser;
            setPendingCloser(null);
            await applyStatusMove({
              clientId: p.client.id, toStatus: p.toStatus,
              fromStatus: p.fromStatus,
              moved: { client: p.client, status: p.fromStatus },
              closerId,
            });
          }} />
      )}
    </div>
  );
}

function KanbanColumn({ status, items, movingId, onCardClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: status.key });
  return (
    <div ref={setNodeRef}
         data-testid={`kanban-col-${status.key}`}
         style={{ minWidth: "260px", maxWidth: "260px",
                   background: isOver ? "#fef7e5" : "#fafafa",
                   borderRadius: "8px", padding: "12px",
                   border: `1px solid ${isOver ? "#C5A059" : "#e5e7eb"}`,
                   transition: "all 0.2s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: "Manrope",
                       fontSize: "12px", fontWeight: 700, color: "#0A0A0A" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: status.color }} />
          {status.label}
        </span>
        <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "#999",
                       background: "white", padding: "2px 8px", borderRadius: "10px" }}>
          {items.length}
        </span>
      </div>
      <SortableContext items={items.map(c => c.id)} strategy={verticalListSortingStrategy}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", minHeight: "40px" }}>
          {items.slice(0, 30).map(c => (
            <KanbanCard key={c.id} client={c} isMoving={movingId === c.id} onCardClick={onCardClick} />
          ))}
          {items.length === 0 && (
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#bbb", textAlign: "center",
                          padding: "20px 0", fontStyle: "italic" }}>
              Перетащите сюда
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

function KanbanCard({ client, isMoving, onCardClick }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: client.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    background: "white", border: "1px solid #e5e7eb", borderRadius: "6px",
    padding: "8px 10px",
    cursor: isDragging ? "grabbing" : "grab",
    touchAction: "none",
    boxShadow: isMoving ? "0 0 0 2px #C5A059" : "none",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
         data-testid={`kanban-card-${client.id}`}
         onClick={(e) => { if (!isDragging) onCardClick?.(client.id); e.stopPropagation(); }}>
      <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 600 }}>{client.name}</div>
      <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#999" }}>{client.phone}</div>
      {client.crm_interested_in && (
        <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#666", marginTop: "4px",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {client.crm_interested_in}
        </div>
      )}
      {client.crm_size && (
        <span style={{ display: "inline-block", marginTop: "4px",
                       fontSize: "10px", padding: "1px 6px", borderRadius: "3px",
                       background: "#f1f5f9", color: "#666", fontFamily: "Manrope" }}>
          {client.crm_size}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// TASKS
// ============================================================================

function TasksPane({ meta, managers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", manager_id: "", only_mine: false });
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.manager_id) params.manager_id = filters.manager_id;
    if (filters.only_mine) params.only_mine = true;
    try {
      const r = await axios.get(`${API}/admin/crm/tasks`, { params, withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.manager_id, filters.only_mine]);

  return (
    <div>
      <div className="crm-filter-bar" style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginBottom: "18px" }}>
        <select data-testid="task-status-filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={selStyle}>
          <option value="">Все задачи</option>
          <option value="pending">Активные</option>
          <option value="overdue">Просроченные</option>
          <option value="done">Выполненные</option>
        </select>
        {isPrivileged && (
          <select value={filters.manager_id} onChange={(e) => setFilters({ ...filters, manager_id: e.target.value })} style={selStyle}>
            <option value="">Все менеджеры</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: "Manrope", fontSize: "12px", color: "#666", cursor: "pointer" }}>
          <input type="checkbox" checked={filters.only_mine} onChange={(e) => setFilters({ ...filters, only_mine: e.target.checked })} />
          Только мои
        </label>
        <div style={{ flex: 1 }} />
        <button onClick={load} style={btnSecondary}><RefreshCw size={13} /> Обновить</button>
      </div>

      {loading ? <Loading /> : items.length === 0 ? <Empty text="Задач нет" /> : (
        <div className="crm-tasks-table-wrap" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "auto" }}>
          <table className="crm-tasks-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: "780px" }}>
            <thead>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #e5e7eb" }}>
                <Th /><Th>Задача</Th><Th>Клиент</Th><Th>Менеджер</Th><Th>Срок</Th><Th />
              </tr>
            </thead>
            <tbody>
              {items.map(t => <TaskTableRow key={t.id} task={t} onChange={load} managers={managers} meta={meta} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TASK COMPLETE MODAL — задачу нельзя закрыть без результата и следующего шага
// ============================================================================

function TaskCompleteModal({ task, meta, onClose }) {
  const [form, setForm] = useState({ result: "", next_task_title: "", next_task_type: "call", next_task_due_at: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const resultOk = form.result.trim().length >= 5;
  const valid = resultOk && form.next_task_title.trim() && form.next_task_due_at;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true); setError("");
    try {
      await axios.patch(`${API}/admin/crm/tasks/${task.id}`, {
        status: "done",
        result: form.result.trim(),
        next_task_title: form.next_task_title.trim(),
        next_task_type: form.next_task_type,
        next_task_due_at: new Date(form.next_task_due_at).toISOString(),
      }, { withCredentials: true });
      onClose(true);
      return;
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось закрыть задачу");
    }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 130, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
         onClick={() => onClose(false)}>
      <div data-testid="task-complete-modal" onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", fontWeight: 400 }}>
            Выполнить: {task.title}
          </h3>
          <button data-testid="task-complete-close" onClick={() => onClose(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
        </div>
        {task.client_name && (
          <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#888", marginBottom: "10px" }}>
            Лид: {task.client_name}{task.client_phone ? ` · ${task.client_phone}` : ""}
          </div>
        )}
        <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#92400E", background: "#FFFBEB",
                      border: "1px solid #FDE68A", borderRadius: "6px", padding: "10px 12px", marginBottom: "16px", lineHeight: 1.5 }}>
          По лиду всегда должна стоять следующая задача. Опишите договорённости и
          назначьте следующий шаг — только после этого задача закроется.
        </div>

        <Field label="Результат: договорённости и пройденные этапы *">
          <textarea data-testid="task-result-input" value={form.result}
                    onChange={(e) => setForm({ ...form, result: e.target.value })}
                    placeholder="Например: была на примерке — ушла думать над пальто серого цвета, размер XL, без меха (Italia Grandi)"
                    style={{ width: "100%", minHeight: "80px", padding: "10px", border: `1px solid ${form.result && !resultOk ? "#FCA5A5" : "#e5e7eb"}`,
                             borderRadius: "6px", fontFamily: "Manrope", fontSize: "13px", resize: "vertical" }} />
        </Field>
        <Field label="Следующая задача по лиду *">
          <Inp data-testid="task-next-title-input" value={form.next_task_title}
               onChange={(v) => setForm({ ...form, next_task_title: v })}
               placeholder="Например: связаться через 3 дня — напомнить о себе, узнать о намерении" />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <Field label="Тип задачи">
            <select data-testid="task-next-type" value={form.next_task_type}
                    onChange={(e) => setForm({ ...form, next_task_type: e.target.value })}
                    style={{ ...selStyle, width: "100%" }}>
              {(meta?.task_types || [{ key: "call", label: "Звонок" }, { key: "custom", label: "Задача" }]).map(t =>
                <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Когда *">
            <Inp data-testid="task-next-due-input" type="datetime-local" value={form.next_task_due_at}
                 onChange={(v) => setForm({ ...form, next_task_due_at: v })} />
          </Field>
        </div>

        {error && <div data-testid="task-complete-error" style={{ fontFamily: "Manrope", fontSize: "12px", color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "6px", padding: "8px 10px", marginTop: "4px" }}>{error}</div>}

        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          <button data-testid="task-complete-submit" onClick={submit} disabled={!valid || saving}
                  style={{ ...btnPrimary, opacity: !valid || saving ? 0.5 : 1, cursor: !valid ? "not-allowed" : "pointer" }}>
            <Check size={13} /> {saving ? "Сохранение…" : "Выполнено + следующий шаг"}
          </button>
          <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

function TaskTableRow({ task, onChange, managers, meta }) {
  const [showComplete, setShowComplete] = useState(false);
  const toggle = async () => {
    if (task.status !== "done") { setShowComplete(true); return; }
    await axios.patch(`${API}/admin/crm/tasks/${task.id}`, { status: "pending" }, { withCredentials: true });
    onChange();
  };
  const remove = async () => {
    if (!window.confirm("Удалить задачу?")) return;
    await axios.delete(`${API}/admin/crm/tasks/${task.id}`, { withCredentials: true });
    onChange();
  };

  const overdue = task.is_overdue;
  const done = task.status === "done";
  const mc = managerColor(task.assigned_manager_id);

  return (
    <tr data-testid={`task-row-${task.id}`} style={{ borderBottom: "1px solid #f1f5f9",
                                                       background: overdue && !done ? "#fef2f2" : (done ? "white" : mc.bg),
                                                       borderLeft: `4px solid ${done ? "#10b981" : mc.stripe}` }}>
      <Td className="crm-tasks-td-primary" style={{ width: "40px" }}>
        <button data-testid={`task-toggle-${task.id}`} onClick={toggle}
                style={{ width: "22px", height: "22px", borderRadius: "50%",
                         border: `2px solid ${done ? "#10b981" : "#cbd5e1"}`,
                         background: done ? "#10b981" : "white", cursor: "pointer",
                         display: "flex", alignItems: "center", justifyContent: "center" }}>
          {done && <Check size={12} color="white" />}
        </button>
      </Td>
      <Td className="crm-tasks-td-primary">
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, textDecoration: done ? "line-through" : "none", color: done ? "#999" : "#0A0A0A" }}>
            {task.title}
          </span>
          {task.auto && (
            <span title="Создано автоматически" data-testid={`task-auto-badge-${task.id}`}
                  style={{ background: "#fef3c7", color: "#92400e", fontSize: "9px",
                           padding: "2px 6px", borderRadius: "10px", fontWeight: 700,
                           letterSpacing: "0.05em", textTransform: "uppercase" }}>
              AUTO
            </span>
          )}
        </div>
        {task.task_type_label && task.task_type !== "custom" && (
          <div style={{ fontSize: "11px", color: "#999" }}>{task.task_type_label}</div>
        )}
        {task.comment && <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{task.comment}</div>}
      </Td>
      <Td label="Клиент">
        <div style={{ fontSize: "13px" }}>{task.client_name || "—"}</div>
        <div style={{ fontSize: "11px", color: "#999" }}>{task.client_phone}</div>
      </Td>
      <Td label="Менеджер" style={{ fontSize: "12px", color: "#666" }}>{task.assigned_manager_name || "—"}</Td>
      <Td label="Срок">
        <div style={{ display: "flex", alignItems: "center", gap: "4px", color: overdue && !done ? "#dc2626" : "#374151" }}>
          {overdue && !done && <AlertCircle size={13} />}
          <span style={{ fontWeight: overdue && !done ? 700 : 400 }}>{fmtDateTime(task.due_at)}</span>
        </div>
      </Td>
      <Td style={{ width: "40px" }}>
        <button onClick={remove} style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb" }}>
          <Trash2 size={14} />
        </button>
      </Td>
      {showComplete && (
        <Td style={{ padding: 0, width: 0 }}>
          <TaskCompleteModal task={task} meta={meta}
                             onClose={(saved) => { setShowComplete(false); if (saved) onChange(); }} />
        </Td>
      )}
    </tr>
  );
}

function TaskRow({ task, onChange, meta }) {
  const [showComplete, setShowComplete] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const toggle = async () => {
    if (task.status !== "done") { setShowComplete(true); return; }
    await axios.patch(`${API}/admin/crm/tasks/${task.id}`, { status: "pending" }, { withCredentials: true });
    onChange();
  };
  const dismiss = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Снять задачу «${task.title}»?\n\nЭто отменит её без создания новой (используйте для дубликатов и неактуальных задач).`)) return;
    setDismissing(true);
    try {
      await axios.post(`${API}/admin/crm/tasks/${task.id}/dismiss`, {}, { withCredentials: true });
      onChange();
    } catch (err) {
      alert(err.response?.data?.detail || "Не удалось снять задачу");
    } finally { setDismissing(false); }
  };
  const overdue = task.is_overdue;
  const done = task.status === "done";
  const cancelled = task.status === "cancelled";

  return (
    <div data-testid={`task-row-${task.id}`} style={{ display: "flex", alignItems: "flex-start", gap: "10px",
                                                       padding: "10px", marginBottom: "6px",
                                                       background: cancelled ? "#f5f5f5" : (overdue && !done ? "#fef2f2" : "#fafafa"),
                                                       border: "1px solid #e5e7eb", borderRadius: "6px",
                                                       opacity: cancelled ? 0.6 : 1 }}>
      <button onClick={toggle} disabled={cancelled}
              style={{ width: "22px", height: "22px", borderRadius: "50%",
                       border: `2px solid ${done ? "#10b981" : "#cbd5e1"}`,
                       background: done ? "#10b981" : "white",
                       cursor: cancelled ? "not-allowed" : "pointer",
                       display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {done && <Check size={12} color="white" />}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontFamily: "Manrope", fontSize: "13px",
                       color: (done || cancelled) ? "#999" : "#0A0A0A",
                       textDecoration: (done || cancelled) ? "line-through" : "none" }}>
          {task.title}
          {cancelled && <span style={{ marginLeft: 8, fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>· снята</span>}
        </div>
        {task.comment && <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{task.comment}</div>}
        {done && task.result && (
          <div style={{ fontSize: "12px", color: "#047857", marginTop: "2px" }}>✓ {task.result}</div>
        )}
        {cancelled && task.result && (
          <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>{task.result}</div>
        )}
        <div style={{ fontSize: "11px", color: overdue && !done && !cancelled ? "#dc2626" : "#999", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
          {overdue && !done && !cancelled && <AlertCircle size={11} />}
          <Clock size={11} /> {fmtDateTime(task.due_at)}
        </div>
      </div>
      {!done && !cancelled && (
        <button data-testid={`task-dismiss-${task.id}`} onClick={dismiss} disabled={dismissing}
          title="Снять задачу (без создания новой)"
          style={{ background: "transparent", border: "none",
                    cursor: dismissing ? "not-allowed" : "pointer",
                    color: "#94a3b8", padding: 4, opacity: dismissing ? 0.4 : 1 }}>
          <X size={16} />
        </button>
      )}
      {showComplete && (
        <TaskCompleteModal task={task} meta={meta}
                           onClose={(saved) => { setShowComplete(false); if (saved) onChange(); }} />
      )}
    </div>
  );
}

// ============================================================================
// TASK / LEAD FORMS
// ============================================================================

function TaskForm({ clientId, managers, meta, onClose }) {
  const [form, setForm] = useState({
    task_type: "call", title: "", due_at: "", comment: "",
    assigned_manager_id: meta.user_id,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.assigned_manager_id) return;
    setSaving(true);
    try {
      const body = {
        user_id: clientId, assigned_manager_id: form.assigned_manager_id,
        task_type: form.task_type,
        title: form.title || undefined,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : undefined,
        comment: form.comment,
      };
      await axios.post(`${API}/admin/crm/tasks`, body, { withCredentials: true });
      onClose(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center" }}
         onClick={() => onClose(false)}>
      <div data-testid="crm-task-form" onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "480px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", fontWeight: 400 }}>Новая задача</h3>
          <button onClick={() => onClose(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
        </div>

        <Field label="Тип задачи">
          <select data-testid="task-type-select" value={form.task_type} onChange={(e) => setForm({ ...form, task_type: e.target.value })} style={{ ...selStyle, width: "100%" }}>
            {meta.task_types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Описание (если не стандарт)">
          <Inp data-testid="task-title-input" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="(необязательно)" />
        </Field>
        <Field label="Срок">
          <Inp data-testid="task-due-input" type="datetime-local" value={form.due_at} onChange={(v) => setForm({ ...form, due_at: v })} />
        </Field>
        <Field label="Ответственный менеджер">
          <select data-testid="task-manager-select" value={form.assigned_manager_id}
                  onChange={(e) => setForm({ ...form, assigned_manager_id: e.target.value })}
                  style={{ ...selStyle, width: "100%" }}>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="Комментарий">
          <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                    style={{ width: "100%", minHeight: "60px", padding: "8px", border: "1px solid #e5e7eb", borderRadius: "6px",
                             fontFamily: "Manrope", fontSize: "13px" }} />
        </Field>

        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          <button data-testid="task-save-btn" onClick={submit} disabled={saving} style={btnPrimary}>
            {saving ? "Сохранение…" : "Создать задачу"}
          </button>
          <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CLAIM MODAL — стать ответственным можно только с результатом первого контакта
// ============================================================================

function ClaimModal({ client, meta, onClose }) {
  const [form, setForm] = useState({ contact_result: "", next_task_type: "call", next_task_title: "", next_task_due_at: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const resultOk = form.contact_result.trim().length >= 10;
  const valid = resultOk && form.next_task_title.trim() && form.next_task_due_at;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/admin/crm/clients/${client.id}/claim`, {
        contact_result: form.contact_result.trim(),
        next_task_title: form.next_task_title.trim(),
        next_task_type: form.next_task_type,
        next_task_due_at: new Date(form.next_task_due_at).toISOString(),
      }, { withCredentials: true });
      onClose(true);
      return;
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось взять клиента");
    }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
         onClick={() => onClose(false)}>
      <div data-testid="crm-claim-modal" onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", fontWeight: 400 }}>
            Взять клиента: {client.name || client.phone}
          </h3>
          <button data-testid="crm-claim-close" onClick={() => onClose(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#92400E", background: "#FFFBEB",
                      border: "1px solid #FDE68A", borderRadius: "6px", padding: "10px 12px", marginBottom: "16px", lineHeight: 1.5 }}>
          Сначала свяжитесь с клиентом. Ответственным вы станете только после заполнения
          результата первого контакта и договорённости о следующем шаге.
        </div>

        <Field label="Результат первого контакта *">
          <textarea data-testid="crm-claim-result-input" value={form.contact_result}
                    onChange={(e) => setForm({ ...form, contact_result: e.target.value })}
                    placeholder="Что выяснили: что хочет клиент, размер, бюджет, возражения…"
                    style={{ width: "100%", minHeight: "80px", padding: "10px", border: `1px solid ${form.contact_result && !resultOk ? "#FCA5A5" : "#e5e7eb"}`,
                             borderRadius: "6px", fontFamily: "Manrope", fontSize: "13px", resize: "vertical" }} />
          {form.contact_result && !resultOk && (
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#DC2626", marginTop: "2px" }}>Минимум 10 символов — опишите содержательно</div>
          )}
        </Field>
        <Field label="Следующая задача — о чём договорились *">
          <Inp data-testid="crm-claim-task-input" value={form.next_task_title}
               onChange={(v) => setForm({ ...form, next_task_title: v })}
               placeholder="Например: перезвонить по наличию 46 размера" />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <Field label="Тип задачи">
            <select data-testid="crm-claim-task-type" value={form.next_task_type}
                    onChange={(e) => setForm({ ...form, next_task_type: e.target.value })}
                    style={{ ...selStyle, width: "100%" }}>
              {(meta.task_types || []).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Когда *">
            <Inp data-testid="crm-claim-due-input" type="datetime-local" value={form.next_task_due_at}
                 onChange={(v) => setForm({ ...form, next_task_due_at: v })} />
          </Field>
        </div>

        {error && <div data-testid="crm-claim-error" style={{ fontFamily: "Manrope", fontSize: "12px", color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "6px", padding: "8px 10px", marginTop: "4px" }}>{error}</div>}

        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          <button data-testid="crm-claim-submit" onClick={submit} disabled={!valid || saving}
                  style={{ ...btnPrimary, opacity: !valid || saving ? 0.5 : 1, cursor: !valid ? "not-allowed" : "pointer" }}>
            <Check size={13} /> {saving ? "Сохранение…" : "Стать ответственным"}
          </button>
          <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

function CreateLead({ managers, onClose, onOpenExisting }) {
  const [form, setForm] = useState({ name: "", phone: "", city: "", size: "", interested_in: "", notes: "", manager_id: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dupeCheck, setDupeCheck] = useState({ checking: false, client: null });

  // Live duplicate check when the manager tabs out of the phone input.
  // Uses the server's `/clients/lookup` which applies exact `normalize_phone`
  // matching so "8XXX", "7XXX", "+7XXX" all resolve to the same E.164 form.
  const checkDuplicate = async () => {
    const raw = form.phone.trim();
    if (!raw) { setDupeCheck({ checking: false, client: null }); return; }
    setDupeCheck({ checking: true, client: null });
    try {
      const r = await axios.get(`${API}/admin/crm/clients/lookup`, {
        params: { phone: raw }, withCredentials: true,
      });
      if (r.data?.found) {
        setDupeCheck({ checking: false, client: r.data.client });
      } else {
        setDupeCheck({ checking: false, client: null });
      }
    } catch { setDupeCheck({ checking: false, client: null }); }
  };

  const submit = async () => {
    if (!form.phone || !form.name) { setErr("Имя и телефон обязательны"); return; }
    setSaving(true);
    setErr("");
    try {
      const r = await axios.post(`${API}/admin/crm/clients`, form, { withCredentials: true });
      if (r.data?.duplicate && r.data?.lead?.id) {
        // Server merged into an existing user — surface it and jump to the card.
        alert(`Клиент с таким номером уже существует: ${r.data.lead.name || "без имени"}.\nОткрываю его карточку.`);
        onOpenExisting?.(r.data.lead.id);
      } else {
        onClose(true);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка");
    }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center" }}
         onClick={() => onClose(false)}>
      <div data-testid="crm-create-lead-form" onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "480px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", fontWeight: 400 }}>Новый лид</h3>
          <button onClick={() => onClose(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
        </div>
        <Field label="Имя клиента *"><Inp data-testid="lead-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
        <Field label="Телефон *">
          <Inp data-testid="lead-phone-input" value={form.phone}
               onChange={(v) => { setForm({ ...form, phone: v }); if (dupeCheck.client) setDupeCheck({ checking: false, client: null }); }}
               onBlur={checkDuplicate}
               placeholder="+7 777 000 00 00" />
        </Field>
        {dupeCheck.checking && (
          <div style={{ marginTop: "-8px", marginBottom: "10px", fontSize: "12px", color: "#666", fontFamily: "Manrope" }}>
            Проверяю базу…
          </div>
        )}
        {dupeCheck.client && (
          <div data-testid="lead-duplicate-warning"
               style={{ marginTop: "-6px", marginBottom: "12px", padding: "10px 12px",
                        background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "6px",
                        fontFamily: "Manrope", fontSize: "12.5px", color: "#9a3412",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
            <span>
              <b>Такой клиент уже есть:</b> {dupeCheck.client.name || "без имени"} · {dupeCheck.client.phone}
            </span>
            <button type="button" data-testid="lead-open-existing-btn"
                    onClick={() => onOpenExisting?.(dupeCheck.client.id)}
                    style={{ background: "#C5A059", color: "#0A0A0A", border: "none", padding: "6px 12px",
                             borderRadius: "4px", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700,
                             letterSpacing: "0.05em", cursor: "pointer", whiteSpace: "nowrap" }}>
              Открыть карточку
            </button>
          </div>
        )}
        <Field label="Город"><Inp value={form.city} onChange={(v) => setForm({ ...form, city: v })} /></Field>
        <Field label="Размер"><Inp value={form.size} onChange={(v) => setForm({ ...form, size: v })} /></Field>
        <Field label="Интересующий товар"><Inp value={form.interested_in} onChange={(v) => setForm({ ...form, interested_in: v })} /></Field>
        <Field label="Ответственный менеджер">
          <select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} style={{ ...selStyle, width: "100%" }}>
            <option value="">— Не назначен —</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="Первая заметка">
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    style={{ width: "100%", minHeight: "60px", padding: "8px", border: "1px solid #e5e7eb",
                             borderRadius: "6px", fontFamily: "Manrope", fontSize: "13px" }} />
        </Field>
        {err && <div style={{ color: "#dc2626", fontSize: "12px", marginBottom: "8px" }}>{err}</div>}
        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          <button data-testid="lead-save-btn" onClick={submit} disabled={saving} style={btnPrimary}>
            {saving ? "Сохранение…" : "Создать"}
          </button>
          <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ISSUE OTP MANUALLY (workaround for undeliverable SMS)
// ============================================================================

function IssueOtpModal({ onClose }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { phone, code, expires_at, ttl_minutes }

  const submit = async () => {
    setError("");
    if (!phone.trim()) { setError("Введите телефон"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/auth/issue-otp`, { phone: phone.trim() }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось выдать код");
    } finally {
      setBusy(false);
    }
  };

  const waLink = result
    ? `https://wa.me/${(result.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Здравствуйте! Ваш код для входа в Angiolini VIP: ${result.code}\n\nКод действует 15 минут. Введите его в приложении.`)}`
    : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center" }}
         onClick={onClose}>
      <div data-testid="issue-otp-modal" style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "480px" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>
            <KeyRound size={18} style={{ display: "inline", marginRight: 8, verticalAlign: "text-bottom" }} />
            Выдать код вручную
          </h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748b" }}>
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <>
            <p style={{ fontFamily: "Manrope", fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 14 }}>
              Используйте, если клиенту не приходит SMS (оператор не доставляет, номер в стоп-листе и т.п.).
              Код сгенерируется на сервере, продиктуйте его клиенту по WhatsApp/телефону. Код действует <strong>15 минут</strong>.
            </p>
            <label style={{ display: "block", fontFamily: "Manrope", fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Телефон клиента
              <input
                data-testid="issue-otp-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+7 701 000 00 00"
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", marginTop: 6, fontFamily: "Manrope", fontSize: 14, outline: "none" }}
              />
            </label>
            {error && <div data-testid="issue-otp-error" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "8px 12px", borderRadius: 6, marginTop: 10, fontSize: 13, fontFamily: "Manrope" }}>{error}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
              <button onClick={onClose} style={btnSecondary}>Отмена</button>
              <button data-testid="issue-otp-submit" onClick={submit} disabled={busy} style={btnPrimary}>
                {busy ? "Генерируем…" : "Сгенерировать код"}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="issue-otp-result">
            <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#78350f", marginBottom: 6 }}>
                Код для <strong>{result.phone}</strong> (действует {result.ttl_minutes} минут):
              </div>
              <div data-testid="issue-otp-code-display" style={{ fontFamily: "monospace", fontSize: 36, fontWeight: 700, color: "#111", letterSpacing: 8, textAlign: "center", padding: "8px 0", background: "white", borderRadius: 6, userSelect: "all" }}>
                {result.code}
              </div>
            </div>
            <p style={{ fontFamily: "Manrope", fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 14 }}>
              Передайте код клиенту удобным способом. Клиент вводит его на экране OTP в приложении.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a data-testid="issue-otp-whatsapp" href={waLink} target="_blank" rel="noopener noreferrer"
                 style={{ ...btnPrimary, background: "#25D366", color: "white", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <MessageCircle size={14} /> Отправить в WhatsApp
              </a>
              <button
                data-testid="issue-otp-copy"
                onClick={() => { navigator.clipboard?.writeText(result.code); }}
                style={btnSecondary}
              >
                Скопировать код
              </button>
              <button onClick={onClose} style={{ ...btnSecondary, marginLeft: "auto" }}>Закрыть</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// STATS
// ============================================================================

function StatsPane({ meta }) {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning] = useState(false);
  const isPrivileged = ["admin", "senior_manager"].includes(meta.role);
  const isAdmin = meta.role === "admin";
  const [resetting, setResetting] = useState("");

  const loadStats = () => {
    setLoading(true);
    axios.get(`${API}/admin/crm/stats?period=${period}`, { withCredentials: true })
      .then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const resetTasks = async (m) => {
    const open = m.tasks_pending || 0;
    if (!open) { alert(`У ${m.name} нет открытых задач.`); return; }
    if (!window.confirm(
      `Сбросить ${open} открытых задач у ${m.name}?\n\n` +
      `Задачи будут удалены безвозвратно. Выполненные задачи и статистика останутся. ` +
      `Действие запишется в журнал.`)) return;
    setResetting(m.id);
    try {
      const r = await axios.post(`${API}/admin/crm/managers/${m.id}/tasks/reset`, {},
        { withCredentials: true });
      alert(`Удалено задач: ${r.data.deleted} · ${r.data.manager}`);
      loadStats();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось сбросить задачи");
    }
    setResetting("");
  };

  const runAutomation = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/crm/automation/run`, {}, { withCredentials: true });
      setRunResult(r.data);
    } catch (e) {
      setRunResult({ error: e.response?.data?.detail || "Ошибка" });
    }
    setRunning(false);
  };

  if (loading || !data) return <Loading />;
  const s = data.summary;

  return (
    <div>
      <div style={{ marginBottom: "20px", display: "flex", gap: "8px", alignItems: "center" }}>
        <span style={{ fontFamily: "Manrope", fontSize: "12px", color: "#666" }}>Период:</span>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} style={selStyle}>
          <option value="today">Сегодня</option>
          <option value="7d">7 дней</option>
          <option value="30d">30 дней</option>
          <option value="all">Всё время</option>
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "24px" }}>
        <Stat label="Новые заявки" value={s.new} color="#3b82f6" />
        <Stat label="В работе" value={s.in_progress} color="#8b5cf6" />
        <Stat label="Записаны на примерку" value={s.appointment_booked} color="#f59e0b" />
        <Stat label="Купили" value={s.purchased} color="#10b981" />
        <Stat label="Отказы" value={s.refused} color="#ef4444" />
        <Stat label="Активная воронка" value={s.active_total} color="#0A0A0A" />
        <Stat label="Просроченные задачи" value={s.overdue_tasks} color="#dc2626" warn={s.overdue_tasks > 0} />
        <Stat label="Записи (период)" value={s.appointments} color="#06b6d4" />
      </div>

      {/* Automation control (admin only) */}
      {isAdmin && (
        <div data-testid="crm-automation-block"
             style={{ background: "linear-gradient(135deg,#FEF7E5 0%,#FFF 100%)",
                      border: "1px solid #F5E6B8", borderRadius: "10px",
                      padding: "16px 20px", marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                         flexWrap: "wrap", gap: "12px" }}>
            <div style={{ flex: "1 1 320px" }}>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: "18px", color: "#92400e",
                             marginBottom: "6px", fontWeight: 500 }}>
                ⚡ Автоматизация задач
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#666", lineHeight: 1.6 }}>
                Система сама создаёт задачи менеджерам по событиям:<br/>
                • Регистрация клиента → <em>«Позвонить»</em> через 1 час<br/>
                • Запись на примерку → <em>«Напомнить»</em> за 2 часа до встречи<br/>
                • Клиент в статусе «Думает» 7+ дней → <em>«Дожать»</em> (cron 10:00)<br/>
                • Клиент не пришёл на примерку → <em>«Уточнить»</em> (cron каждый час)
              </div>
            </div>
            <button data-testid="crm-run-automation-btn" onClick={runAutomation} disabled={running}
                    style={{ ...btnPrimary, alignSelf: "center" }}>
              {running ? "Выполняется…" : "Запустить вручную"}
            </button>
          </div>
          {runResult && (
            <div data-testid="crm-automation-result"
                 style={{ marginTop: "12px", padding: "10px 14px", background: "white",
                           borderRadius: "6px", fontFamily: "Manrope", fontSize: "12px" }}>
              {runResult.error ? (
                <span style={{ color: "#dc2626" }}>Ошибка: {runResult.error}</span>
              ) : (
                <span style={{ color: "#15803d" }}>
                  ✓ Создано задач: <strong>{runResult.thinking_followups_created}</strong> «Дожать»,{" "}
                  <strong>{runResult.no_show_followups_created}</strong> «Уточнить про неприход»
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Telegram integration */}
      <TelegramConnectBlock />

      {/* Funnel breakdown */}
      <SectionTitle>Воронка</SectionTitle>
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "16px", marginBottom: "24px" }}>
        {data.funnel.map(f => (
          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 0",
                                     borderBottom: "1px solid #f1f5f9" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: f.color }} />
            <span style={{ flex: 1, fontFamily: "Manrope", fontSize: "13px" }}>{f.label}</span>
            <span style={{ fontFamily: "Manrope", fontSize: "14px", fontWeight: 700 }}>{f.count}</span>
          </div>
        ))}
      </div>

      {/* Per-manager */}
      {isPrivileged && data.per_manager && data.per_manager.length > 0 && (
        <>
          <SectionTitle>Эффективность менеджеров</SectionTitle>
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafafa", borderBottom: "1px solid #e5e7eb" }}>
                  <Th>Менеджер</Th><Th>Активные</Th><Th>Купили</Th><Th>Отказы</Th>
                  <Th>Конверсия</Th><Th>Задач выполнено</Th><Th>В работе</Th><Th>Просрочено</Th>
                  {isAdmin && <Th>Сброс</Th>}
                </tr>
              </thead>
              <tbody>
                {data.per_manager.map(m => (
                  <tr key={m.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <Td>{m.name} <span style={{ color: "#999", fontSize: "11px" }}>({m.role})</span></Td>
                    <Td>{m.active_clients}</Td>
                    <Td style={{ color: "#10b981", fontWeight: 600 }}>{m.purchased}</Td>
                    <Td style={{ color: "#ef4444" }}>{m.refused}</Td>
                    <Td><span style={{ fontWeight: 700, color: "#C5A059" }}>{m.conversion_pct}%</span></Td>
                    <Td>{m.tasks_done}</Td>
                    <Td>{m.tasks_pending}</Td>
                    <Td style={{ color: m.tasks_overdue > 0 ? "#dc2626" : "#999", fontWeight: m.tasks_overdue > 0 ? 700 : 400 }}>
                      {m.tasks_overdue}
                    </Td>
                    {isAdmin && (
                      <Td>
                        <button
                          data-testid={`crm-reset-tasks-${m.id}`}
                          onClick={() => resetTasks(m)}
                          disabled={resetting === m.id || !m.tasks_pending}
                          title="Удалить все открытые задачи сотрудника"
                          style={{
                            fontFamily: "Manrope", fontSize: "11px", padding: "5px 10px",
                            borderRadius: "999px", cursor: m.tasks_pending ? "pointer" : "not-allowed",
                            border: `1px solid ${m.tasks_pending ? "#dc2626" : "#e5e7eb"}`,
                            background: "white", color: m.tasks_pending ? "#dc2626" : "#bbb",
                            whiteSpace: "nowrap", transition: "background-color .15s, color .15s",
                          }}>
                          {resetting === m.id ? "Сброс…" : `Сбросить (${m.tasks_pending})`}
                        </button>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Shared
// ============================================================================

const Loading = () => <div style={{ textAlign: "center", padding: "40px", color: "#999", fontFamily: "Manrope" }}>Загрузка…</div>;
const Empty = ({ text }) => <div data-testid="crm-empty-state" style={{ textAlign: "center", padding: "60px 0", color: "#999", fontFamily: "Manrope", fontSize: "13px" }}>{text}</div>;
const SectionTitle = ({ children }) => (
  <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#666", textTransform: "uppercase",
                letterSpacing: "0.12em", fontWeight: 700, marginBottom: "10px", marginTop: "12px" }}>{children}</div>
);
const Info = ({ label, children, colSpan }) => (
  <div style={{ gridColumn: colSpan ? `span ${colSpan}` : undefined, background: "#fafafa", borderRadius: "6px", padding: "10px 12px" }}>
    <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#888", letterSpacing: "0.1em",
                  textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
    <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A" }}>{children}</div>
  </div>
);
const Field = ({ label, children }) => (
  <div style={{ marginBottom: "12px" }}>
    <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#666", letterSpacing: "0.08em",
                  textTransform: "uppercase", marginBottom: "5px", fontWeight: 600 }}>{label}</div>
    {children}
  </div>
);
const Th = ({ children, style = {} }) => (
  <th style={{ textAlign: "left", padding: "10px 14px", fontFamily: "Manrope", fontSize: "10px",
               color: "#888", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
               ...style }}>{children}</th>
);
const Td = ({ children, style = {}, label, className, ...rest }) => (
  <td className={className} data-label={label} {...rest}
      style={{ padding: "10px 14px", fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A", ...style }}>
    {children}
  </td>
);
const Inp = ({ value, onChange, ...rest }) => (
  <input value={value || ""} onChange={(e) => onChange(e.target.value)} {...rest}
         style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: "6px",
                  fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A", background: "white",
                  boxSizing: "border-box" }} />
);
const Stat = ({ label, value, color, warn }) => (
  <div style={{ background: warn ? "#fef2f2" : "white", border: `1px solid ${warn ? "#fecaca" : "#e5e7eb"}`,
                 borderRadius: "10px", padding: "14px 18px" }}>
    <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#888", letterSpacing: "0.12em",
                  textTransform: "uppercase", fontWeight: 700, marginBottom: "4px" }}>{label}</div>
    <div style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", fontWeight: 500, color }}>{value}</div>
  </div>
);
const StatusBadge = ({ color, label }) => (
  <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: "12px",
                  background: `${color}22`, color, fontSize: "11px", fontWeight: 700,
                  fontFamily: "Manrope" }}>{label}</span>
);

// ============================================================================
// Telegram connect block
// ============================================================================

function TelegramConnectBlock() {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/telegram/status`, { withCredentials: true });
      setStatus(r.data);
    } catch { setStatus(null); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/telegram/connect-init`, {}, { withCredentials: true });
      setLink(r.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Ошибка");
    }
    setBusy(false);
  };
  const disconnect = async () => {
    if (!window.confirm("Отвязать Telegram от вашего аккаунта?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/telegram/disconnect`, {}, { withCredentials: true });
      setLink(null);
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Ошибка"); }
    setBusy(false);
  };
  const sendTest = async () => {
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/admin/telegram/test`, {}, { withCredentials: true });
      setMsg("✓ Тестовое сообщение отправлено — проверьте Telegram");
    } catch (e) { setMsg(e.response?.data?.detail || "Ошибка"); }
    setBusy(false);
  };

  if (!status) return null;
  if (!status.configured) {
    return (
      <div style={{ background: "#fafafa", border: "1px dashed #d1d5db", borderRadius: "10px",
                     padding: "14px 18px", marginBottom: "24px", fontFamily: "Manrope",
                     fontSize: "12px", color: "#888" }}>
        💬 Telegram-бот не настроен (нет TOKEN в окружении).
      </div>
    );
  }

  return (
    <div data-testid="telegram-connect-block"
         style={{ background: "linear-gradient(135deg,#E5F3FF 0%,#FFF 100%)",
                  border: "1px solid #B4DAFE", borderRadius: "10px",
                  padding: "16px 20px", marginBottom: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                     flexWrap: "wrap", gap: "12px" }}>
        <div style={{ flex: "1 1 320px" }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: "18px", color: "#1e40af",
                         marginBottom: "6px", fontWeight: 500 }}>
            💬 Telegram-уведомления
          </div>
          <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#666", lineHeight: 1.6 }}>
            Получайте новые задачи и просрочки прямо в Telegram. Подтверждайте выполнение
            одной кнопкой — без открытия админки.<br/>
            Бот: <a href={`https://t.me/${status.bot_username}`} target="_blank" rel="noreferrer"
                     style={{ color: "#1e40af", fontWeight: 600 }}>@{status.bot_username}</a>
          </div>
        </div>
        <div style={{ alignSelf: "center", display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-end" }}>
          {status.connected ? (
            <>
              <span data-testid="tg-status-connected"
                    style={{ display: "flex", alignItems: "center", gap: "6px",
                             color: "#15803d", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700 }}>
                ✓ Подключено{status.telegram_username ? ` (@${status.telegram_username})` : ""}
              </span>
              <div style={{ display: "flex", gap: "6px" }}>
                <button data-testid="tg-test-btn" onClick={sendTest} disabled={busy} style={btnSecondary}>
                  Тест
                </button>
                <button data-testid="tg-disconnect-btn" onClick={disconnect} disabled={busy}
                        style={{ ...btnSecondary, color: "#dc2626", borderColor: "#fecaca" }}>
                  Отвязать
                </button>
              </div>
            </>
          ) : (
            <button data-testid="tg-connect-btn" onClick={connect} disabled={busy}
                    style={{ ...btnPrimary, background: "linear-gradient(135deg,#229ED9,#1E88E5)", color: "white" }}>
              {busy ? "..." : "Подключить Telegram"}
            </button>
          )}
        </div>
      </div>

      {link && !status.connected && (
        <div data-testid="tg-link-instructions"
             style={{ marginTop: "12px", padding: "12px 16px", background: "white",
                       borderRadius: "8px", fontFamily: "Manrope", fontSize: "13px",
                       border: "1px solid #B4DAFE" }}>
          <div style={{ fontWeight: 700, color: "#1e40af", marginBottom: "6px" }}>Как подключить (3 способа):</div>
          <ol style={{ margin: "0 0 12px 20px", padding: 0, lineHeight: 1.55, color: "#374151", fontSize: "12px" }}>
            <li><b>Сотрудник рядом с телефоном:</b> дайте отсканировать QR — откроется Telegram → жмёт Start.</li>
            <li><b>Сотрудник удалённо:</b> «Скопировать ссылку» → отправьте в WhatsApp/SMS.</li>
            <li><b>Подключаете себя:</b> нажмите «Открыть в Telegram» ниже.</li>
          </ol>

          {link.deep_link && (
            <div style={{ display: "flex", justifyContent: "center", padding: "10px", background: "white",
                           border: "1px solid #dbeafe", borderRadius: "8px", marginBottom: "10px" }}>
              <div style={{ textAlign: "center" }}>
                <QRCodeSVG data-testid="tg-qr-code" value={link.deep_link} size={180} level="M" includeMargin={false} />
                <div style={{ fontSize: "10.5px", color: "#6b7280", marginTop: "6px" }}>Наведите камеру телефона</div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
            <a href={link.deep_link || "#"} target="_blank" rel="noreferrer"
               data-testid="tg-deep-link"
               onClick={(e) => {
                 if (!link.deep_link) { e.preventDefault(); setMsg("Ссылка не сгенерирована"); return; }
                 try { window.open(link.deep_link, "_blank", "noopener"); } catch { /* popup blocked */ }
               }}
               style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "10px 16px",
                         background: link.deep_link ? "#229ED9" : "#94a3b8", color: "white", textDecoration: "none",
                         borderRadius: "8px", fontWeight: 700, fontSize: "12px",
                         letterSpacing: "0.03em", cursor: link.deep_link ? "pointer" : "not-allowed" }}>
              <ExternalLink size={13} /> Открыть в Telegram
            </a>
            <button data-testid="tg-copy-link-btn"
                    onClick={() => {
                      try { navigator.clipboard?.writeText(link.deep_link); setMsg("✓ Ссылка скопирована — отправьте её сотруднику"); }
                      catch { setMsg("Не удалось скопировать"); }
                    }}
                    style={{ background: "white", border: "1px solid #B4DAFE", color: "#1e40af",
                              borderRadius: "8px", padding: "9px 14px", fontFamily: "Manrope",
                              fontSize: "12px", fontWeight: 700, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Copy size={13} /> Скопировать ссылку
            </button>
            <button onClick={load}
                    style={{ background: "white", border: "1px solid #e5e7eb", color: "#374151",
                              borderRadius: "8px", padding: "9px 14px", fontFamily: "Manrope",
                              fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
              Обновить статус
            </button>
          </div>
          <div style={{ marginTop: "10px", fontSize: "11px", color: "#888" }}>
            Или вручную: <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: "3px" }}>/start {link.code}</code> боту <b>@{link.bot_username}</b><br/>
            Код действителен до {new Date(link.expires_at).toLocaleTimeString("ru-RU")}.
          </div>
        </div>
      )}

      {msg && (
        <div style={{ marginTop: "10px", fontFamily: "Manrope", fontSize: "12px",
                       color: msg.startsWith("✓") ? "#15803d" : "#dc2626" }}>
          {msg}
        </div>
      )}
    </div>
  );
}
// Auto-prompt modal — shown when a manager moves a client to «Купил»
// without having set a closer. Blocks the status change until either
// a closer is picked (commission attribution) or the manager cancels.
function CloserPromptModal({ client, managers, onConfirm, onCancel }) {
  const [closerId, setCloserId] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!closerId || saving) return;
    setSaving(true);
    try { await onConfirm(closerId); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                  zIndex: 150, display: "flex", alignItems: "center",
                  justifyContent: "center", padding: 16 }}
         onClick={onCancel}>
      <div data-testid="closer-prompt-modal" onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: 24,
                  width: "100%", maxWidth: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, marginBottom: 6, color: "#0A0A0A" }}>
          🎉 Кто закрыл сделку?
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginBottom: 18, lineHeight: 1.5 }}>
          Клиент <b>{client?.name || "—"}</b> перешёл в статус «Купил». Отметьте
          продавца, который физически закрыл сделку в бутике — от этого
          зависит его комиссия.
        </div>
        <select data-testid="closer-prompt-select"
          value={closerId} onChange={(e) => setCloserId(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8,
                    border: "1px solid #d1d5db", fontFamily: "Manrope",
                    fontSize: 14, marginBottom: 18 }}>
          <option value="">— выберите продавца —</option>
          {(managers || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button data-testid="closer-prompt-cancel" onClick={onCancel} disabled={saving}
            style={{ background: "white", border: "1px solid #d1d5db", borderRadius: 6,
                      padding: "9px 16px", cursor: "pointer",
                      fontFamily: "Manrope", fontSize: 13, fontWeight: 600, color: "#374151" }}>
            Отмена
          </button>
          <button data-testid="closer-prompt-confirm" onClick={submit}
            disabled={!closerId || saving}
            style={{ background: (!closerId || saving) ? "#e5e7eb" : "linear-gradient(135deg,#C5A059,#D4B878)",
                      color: "#0A0A0A", border: "none", borderRadius: 6,
                      padding: "9px 20px",
                      cursor: (!closerId || saving) ? "not-allowed" : "pointer",
                      fontFamily: "Manrope", fontSize: 13, fontWeight: 700 }}>
            {saving ? "Сохранение…" : "Подтвердить"}
          </button>
        </div>
      </div>
    </div>
  );
}

const IconBtn = ({ href, children, color, onClick, title, testid }) => (
  <a data-testid={testid} href={href} target="_blank" rel="noreferrer" onClick={onClick} title={title}
     style={{ width: "30px", height: "30px", borderRadius: "6px", background: `${color}15`, color,
              display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
    {children}
  </a>
);

const selStyle = {
  padding: "8px 10px", borderRadius: "6px", border: "1px solid #e5e7eb",
  fontFamily: "Manrope", fontSize: "13px", background: "white", cursor: "pointer",
};
const btnPrimary = {
  display: "flex", alignItems: "center", gap: "6px",
  padding: "8px 16px", border: "none", borderRadius: "6px",
  background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
  cursor: "pointer", fontFamily: "Manrope", fontSize: "13px", fontWeight: 700,
  letterSpacing: "0.05em", textDecoration: "none",
};
const btnSecondary = {
  display: "flex", alignItems: "center", gap: "6px",
  padding: "8px 14px", border: "1px solid #d1d5db", borderRadius: "6px",
  background: "white", color: "#374151", cursor: "pointer",
  fontFamily: "Manrope", fontSize: "13px", textDecoration: "none",
};
